import { EquipmentEditable } from '../equipmentEditable';
import { ElementsList } from './equipmentElementsList';
import { Element } from './element';
import { Point } from '../../../core/models/point';
import { Utils } from '../../../core/services/utils.service';
import { Cylinder } from '../figures/cylinder';
import { Rectangle } from '../figures/rectangle';
import { RectangleSliced } from '../figures/rectangle-sliced';
import { Trapeze } from '../figures/trapeze';
import { Figure } from '../figures/figure';
import { Bounding } from '../../../core/models/bounding';
import { EquipmentElements } from './equipmentElements';
import { EquipmentElement } from './equipmentElement';
import { Extension, ExtensionType, RailsParams, RoundingParams } from './extension';
import { UnitConverter } from '../../../core/utils/unitConverter';
import { GlobalSettings } from '../../../core/models/globalSettings';
import { MeasurementSystem } from '../../../core/models/measurementSystem';

export type EquipmentBases = EquipmentElements.BaseRectangle | EquipmentElements.BaseTrapeze;
export type EquipmentWalls = EquipmentElements.BackWall | EquipmentElements.HookWall | EquipmentElements.PerforatedWall | EquipmentElements.SideWalls | EquipmentElements.Showcase;
export type EquipmentShelves = EquipmentElements.Shelf | EquipmentElements.AngleShelf;
export type EquipmentBoxes = EquipmentElements.Box | EquipmentElements.SlicedBox;

const SHELVES: Array<EquipmentElements> = [EquipmentElements.Shelf, EquipmentElements.AngleShelf],
	WALLS: Array<EquipmentElements> = [
		EquipmentElements.BackWall,
		EquipmentElements.HookWall,
		EquipmentElements.Showcase,
		EquipmentElements.PerforatedWall
	];
const DEFAULT_OPTION = {
	dirty: false,
	disabled: false,
	disabledMessage: '',
	error: '',
	valid: true,
};
const MIN_DIMENSION_VALUE: number = GlobalSettings.MIN_ELEMENT_SIZES.get(MeasurementSystem.Metrics);

type Options = {
	dirty: boolean;
	disabled: boolean;
	disabledMessage: string;
	error: string;
	valid: boolean
	value: number | boolean;
	valueChange: () => void;
}

type ElementValues = {
	width: number;
	frontWidth: number;
	backWidth: number;
	height: number;
	depth: number;
	frontAngle: number;
	rails: boolean;
	railHolesHeight: number;
	railHolesWidth: number;
	distanceBetweenRailHoles: number;
	firstRailHoleDistance: number;
	diameter: number;
	hangBarOffset: number;
	leftWallWidth: number;
	leftWallHeight: number;
	rightWallWidth: number;
	rightWallHeight: number;
	availability: boolean;
	rounding: boolean;
	leftSideRadius: number;
	rightSideRadius: number;
}

type SideWallInfo = {
	begin: Point;
	depth: number;
	angle: number;
	empty: { front: boolean; top: boolean; };
}

type BackWallInfo = {
	begin: Point;
	width: number;
}

interface IElementGenerator {

}

abstract class ElementGenerator implements IElementGenerator {

	static getOptions: (element: Element, equipment: EquipmentEditable) => { [key: string]: Options };
	static getExtensions: () => Array<any> = () => [];
	static updateElement: (element: Element, equipment: EquipmentEditable, values: Partial<ElementValues>) => EquipmentEditable;
	static updateExtensions(element: Element, values: Partial<ElementValues>): void {
		element.extensions.forEach((extension: Extension<any>) => {
			if (values[extension.type] !== undefined) {
				extension.active = values[extension.type];
				Object.keys(extension.params).forEach((param: string) => {
					extension.params[param] = values[param];
				});
			}
		});
	}

	get Element(): Element {
		return new Element(this.parentElement, this.getShapes(), this.getExtensions(), this.dependElement);
	}

	public constructor(
		protected equipment: EquipmentEditable,
		protected parentElement: EquipmentElement,
		protected dependElement: Element = null,
		protected point?: Point
	) {}

	protected abstract getShapes(): Array<Figure>;
	protected abstract getExtensions(): Array<Extension<any>>;
}

abstract class BaseGenerator extends ElementGenerator {
	static getBackWallInfo: (base: Element) => BackWallInfo;
	static getCentralPillarInfo: (base: Element) => BackWallInfo;
	static getShowCaseShapes: (base: Element, height: number) => Array<Figure>;
	static getSidesWallsInfo: (base: Element, width: number) => { left: SideWallInfo; right: SideWallInfo; };
	static getExtensions(): Array<any> {
		return [
			{
				type: ExtensionType.Rounding,
				active: false,
				leftSideRadius: 0,
				rightSideRadius: 0
			},
			{
				type: ExtensionType.Availability,
				active: true
			}
		];
	};

	protected begin: Point = { x: 0, y: 0, z: 0 };

	public constructor(
		protected equipment: EquipmentEditable,
		protected parentElement: EquipmentElement,
		protected dependElement: Element = null,
		protected point?: Point
	) {
		super(equipment, parentElement, dependElement, point);

		if (point != null) this.begin = { ...point };
	}
}

class RectangleBaseGenerator extends BaseGenerator {

	static getOptions(base: Element, equipment: EquipmentEditable): { [key: string]: Options } {
		const availability: boolean = !(base.hasExtension(ExtensionType.Availability) && !base.isExtensionActive(ExtensionType.Availability));
		const rounding: Extension<RoundingParams> = base.getExtension(ExtensionType.Rounding);
		const options: { [key: string]: Options } = {
			length: {
				...DEFAULT_OPTION,
				value: base.shapes[0].width,
				valueChange() {
					if (this.value < MIN_DIMENSION_VALUE) {
						this.valid = false;
						this.error = 'UNABLE_VALUE';
					} else {
						this.valid = true;
						this.error = '';
					}
					this.dirty = this.value != base.shapes[0].width;
				}
			},
			height: {
				...DEFAULT_OPTION,
				value: base.shapes[0].height,
				valueChange() {
					if (this.value < MIN_DIMENSION_VALUE) {
						this.valid = false;
						this.error = 'UNABLE_VALUE';
					} else {
						this.valid = true;
						this.error = '';
					}
					this.dirty = this.value != base.shapes[0].height;
				}
			},
			depth: {
				...DEFAULT_OPTION,
				value: base.shapes[0].depth,
				valueChange() {
					if (this.value < MIN_DIMENSION_VALUE) {
						this.valid = false;
						this.error = 'UNABLE_VALUE';
					} else {
						this.valid = true;
						this.error = '';
					}
					this.dirty = this.value != base.shapes[0].depth;
				}
			},
			rounding: {
				...DEFAULT_OPTION,
				value: rounding?.active,
				valueChange() {
					this.dirty = this.value != rounding?.active;
				}
			},
			leftSideRadius: {
				...DEFAULT_OPTION,
				value: rounding?.params.leftSideRadius,
				valueChange() {
					if (this.value < 0 || this.value > 50) {
						this.valid = false;
						this.error = 'UNABLE_VALUE';
					} else {
						this.valid = true;
						this.error = '';
					}
					this.dirty = this.value != rounding?.params.leftSideRadius;
				}
			},
			rightSideRadius: {
				...DEFAULT_OPTION,
				value: rounding?.params.rightSideRadius,
				valueChange() {
					if (this.value < 0 || this.value > 50) {
						this.valid = false;
						this.error = 'UNABLE_VALUE';
					} else {
						this.valid = true;
						this.error = '';
					}
					this.dirty = this.value != rounding?.params.rightSideRadius;
				}
			},
			availability: {
				...DEFAULT_OPTION,
				value: availability,
				valueChange() {
					this.dirty = this.value != availability;
				}
			}
		};
		const twoSided: boolean = equipment.allElements
			.some((el: Element) => el.parent.is(EquipmentElements.CentralPillar) && el.depend == base);
		const isPallet: boolean = equipment.allElements.every((el: Element) => el.depend != base);

		if (!twoSided && !isPallet) {
			delete options.rounding;
			delete options.leftSideRadius;
			delete options.rightSideRadius;
		}

		return options;
	}

	static getBackWallInfo(base: Element): BackWallInfo {
		const shape: Figure = base.shapes[0];

		return {
			begin: {
				x: shape.begin.x,
				y: Utils.round(shape.begin.y + shape.height),
				z: shape.begin.z
			},
			width: shape.width
		};
	}

	static getCentralPillarInfo(base: Element): BackWallInfo {
		const shape: Figure = base.shapes[0];

		return {
			begin: {
				x: Utils.round(shape.begin.x + shape.width / 2),
				y: Utils.round(shape.begin.y + shape.height),
				z: Utils.round(shape.begin.z + shape.depth / 2)
			},
			width: Math.min(
				shape.width,
				Math.max(
					Utils.round(shape.width - shape.depth),
					UnitConverter.convertAndRoundToSaveValue(0.1)
				)
			)
		};
	}

	static getShowCaseShapes(base: Element, height: number): Array<Figure> {
		const shape: Figure = base.shapes[0],
			rect: Rectangle = new Rectangle({
				begin: {
					x: shape.begin.x,
					y: Utils.round(shape.begin.y + shape.height),
					z: shape.begin.z
				},
				width: shape.width,
				depth: shape.depth,
				height: height,
				priority: 'low',
				empty: {
					front: true,
					top: true
				}
			});

		return [rect];
	}

	static getSidesWallsInfo(base: Element, width: number): { left: SideWallInfo, right: SideWallInfo } {
		const shape: Figure = base.shapes[0];

		return {
			left: {
				begin: {
					x: shape.begin.x,
					y: Utils.round(shape.begin.y + shape.height),
					z: shape.begin.z
				},
				depth: shape.depth,
				angle: 0,
				empty: {
					front: false,
					top: false
				}
			},
			right: {
				begin: {
					x: Utils.round(shape.begin.x + shape.width - width),
					y: Utils.round(shape.begin.y + shape.height),
					z: shape.begin.z
				},
				depth: shape.depth,
				angle: 0,
				empty: {
					front: false,
					top: false
				}
			}
		};
	}

	static updateElement(element: Element, equipment: EquipmentEditable, values: Partial<ElementValues>): EquipmentEditable {
		const heightDelta: number = values.height - element.shapes[0].height;

		if (equipment.allElements.some((el: Element) => el.parent.is(...WALLS) && el.depend == element)) {
			delete values.rounding;
			delete values.leftSideRadius;
			delete values.rightSideRadius;
		}

		element.shapes[0].update(values);
		ElementGenerator.updateExtensions(element, values);

		equipment.allElements
			.filter((el: Element) => el.getDepends().includes(element))
			.forEach((el: Element) => {
				el.shapes.forEach((shape: Figure) => shape.begin.y += heightDelta);

				if (WALLS.includes(el.parent.type)) {
					el.shapes[0].width = values.width;
					if (el.parent.is(EquipmentElements.BackWall)) {
						const rails: Extension<RailsParams> = el.getExtension<RailsParams>(ExtensionType.Rails);
						BackWallGenerator.updateElement(el, equipment, {
							height: el.Height,
							depth: el.Depth,
							rails: rails.active,
							railHolesHeight: rails.params.railHolesHeight,
							railHolesWidth: rails.params.railHolesWidth,
							distanceBetweenRailHoles: rails.params.distanceBetweenRailHoles,
							firstRailHoleDistance: rails.params.firstRailHoleDistance
						})
					}
				}

				if (el.parent.type == EquipmentElements.CentralPillar) {
					CentralPillarGenerator.updateByParent(el, equipment);
				}

				if (el.parent.type == EquipmentElements.Showcase) {
					el.shapes[0].depth = values.depth;
				}

				if (el.parent.type == EquipmentElements.SideWalls) {
					const sidesWallsInfo: { left: SideWallInfo, right: SideWallInfo } = RectangleBaseGenerator.getSidesWallsInfo(element, el.shapes[1].width);

					el.shapes[0].update({
						begin: sidesWallsInfo.left.begin,
						depth: sidesWallsInfo.left.depth
					});
					el.shapes[1].update({
						begin: sidesWallsInfo.right.begin,
						depth: sidesWallsInfo.right.depth
					});
				}
			});

		equipment.allElements
			.filter((el: Element) => el.depend == element)
			.filter((el: Element) => el.parent.is(EquipmentElements.Box, EquipmentElements.SlicedBox, EquipmentElements.Partition))
			.forEach((el: Element) => {
				ElementGenerator.updateExtensions(el, { availability: values.availability });
			});

		return equipment;
	}

	protected getShapes(): Array<Figure> {
		const rect: Rectangle = new Rectangle({
			begin: this.begin,
			height: UnitConverter.convertAndRoundToSaveValue(0.2),
			depth: UnitConverter.convertAndRoundToSaveValue(0.42),
			width: UnitConverter.convertAndRoundToSaveValue(1),
			empty: {
				front: false,
				top: true
			}
		});

		return [rect];
	}

	protected getExtensions(): Array<Extension<any>> {
		return RectangleBaseGenerator.getExtensions().map((extension: any) => {
			const { type, active, ...params } = extension;
			return new Extension<any>(type, active, params);
		});
	}
}

class TrapezeBaseGenerator extends BaseGenerator {

	static getOptions(base: Element, equipment: EquipmentEditable): { [key: string]: Options } {
		const availability: boolean = !(base.hasExtension(ExtensionType.Availability) && !base.isExtensionActive(ExtensionType.Availability));

		return {
			height: {
				...DEFAULT_OPTION,
				value: base.shapes[0].height,
				valueChange() {
					if (this.value < MIN_DIMENSION_VALUE) {
						this.valid = false;
						this.error = 'UNABLE_VALUE';
					} else {
						this.valid = true;
						this.error = '';
					}
					this.dirty = this.value != base.shapes[0].height;
				}
			},
			depth: {
				...DEFAULT_OPTION,
				value: base.shapes[0].depth,
				valueChange() {
					if (this.value < MIN_DIMENSION_VALUE) {
						this.valid = false;
						this.error = 'UNABLE_VALUE';
					} else {
						this.valid = true;
						this.error = '';
					}
					this.dirty = this.value != base.shapes[0].depth;
				}
			},
			frontWidth: {
				...DEFAULT_OPTION,
				value: base.shapes[0].frontWidth,
				valueChange() {
					if (this.value < MIN_DIMENSION_VALUE) {
						this.valid = false;
						this.error = 'UNABLE_VALUE';
					} else {
						this.valid = true;
						this.error = '';
					}
					this.dirty = this.value != base.shapes[0].frontWidth;
				}
			},
			backWidth: {
				...DEFAULT_OPTION,
				value: base.shapes[0].backWidth,
				valueChange() {
					if (this.value < MIN_DIMENSION_VALUE) {
						this.valid = false;
						this.error = 'UNABLE_VALUE';
					} else {
						this.valid = true;
						this.error = '';
					}
					this.dirty = this.value != base.shapes[0].backWidth;
				}
			},
			availability: {
				...DEFAULT_OPTION,
				value: availability,
				valueChange() {
					this.dirty = this.value != availability;
				}
			}
		}
	}

	static getBackWallInfo(base: Element): BackWallInfo {
		const shape: Trapeze = base.shapes[0] as Trapeze;
		let coordinates = shape.getLineCordinates('top');

		return {
			begin: {
				x: coordinates[0][0],
				y: Utils.round(shape.begin.y + shape.height),
				z: coordinates[0][1]
			},
			width: shape.backWidth
		};
	}

	static getShowCaseShapes(element: Element, height: number): Array<Figure> {
		const shape: Figure = element.shapes[0],
			trapeze: Trapeze = new Trapeze({
				begin: {
					x: shape.begin.x,
					y: Utils.round(shape.begin.y + shape.height),
					z: shape.begin.z
				},
				frontWidth: shape.frontWidth,
				backWidth: shape.backWidth,
				depth: shape.depth,
				height: height,
				priority: 'low',
				empty: {
					front: true,
					top: true
				}
			});

		return [trapeze];
	}

	static getSidesWallsInfo(element: Element, width: number): { left: SideWallInfo; right: SideWallInfo; } {
		const shape: Trapeze = element.shapes[0] as Trapeze,
			coordinates = shape.getLineCordinates('top'),
			depth: number = Math.sqrt(Math.pow(coordinates[3][0] - coordinates[0][0], 2)
				+ Math.pow(coordinates[3][1] - coordinates[0][1], 2)),
			angles: { left: number; right: number; } = shape.getSidesAngle(),
			rightBegin: Array<number> = Utils.rotatePoint(
				coordinates[1],
				[coordinates[1][0] - width, shape.begin.z],
				angles.right
			);

		return {
			left: {
				begin: {
					x: coordinates[0][0],
					y: Utils.round(shape.begin.y + shape.height),
					z: shape.begin.z
				},
				depth: depth,
				angle: angles.left,
				empty: {
					front: (shape.backWidth < shape.frontWidth),
					top: false
				}
			},
			right: {
				begin: {
					x: rightBegin[0],
					y: Utils.round(shape.begin.y + shape.height),
					z: rightBegin[1]
				},
				depth: depth,
				angle: angles.right,
				empty: {
					front: (shape.backWidth < shape.frontWidth),
					top: false
				}
			}
		};
	}

	static updateElement(element: Element, equipment: EquipmentEditable, values: Partial<ElementValues>): EquipmentEditable {
		const yDelta: number = values.height - element.shapes[0].height;
		element.shapes[0].update(values);
		ElementGenerator.updateExtensions(element, values);

		equipment.allElements
			.filter((el: Element) => el.getDepends().includes(element))
			.forEach((el: Element) => {
				el.shapes.forEach((shape: Figure) => shape.begin.y += yDelta);

				if (WALLS.includes(el.parent.type)) {
					const backWallInfo: BackWallInfo = TrapezeBaseGenerator.getBackWallInfo(element);
					el.shapes[0].width = backWallInfo.width;
					el.shapes[0].begin = backWallInfo.begin;

					if (el.parent.is(EquipmentElements.BackWall)) {
						const rails: Extension<RailsParams> = el.getExtension<RailsParams>(ExtensionType.Rails);
						BackWallGenerator.updateElement(el, equipment, {
							height: el.Height,
							depth: el.Depth,
							rails: rails.active,
							railHolesHeight: rails.params.railHolesHeight,
							railHolesWidth: rails.params.railHolesWidth,
							distanceBetweenRailHoles: rails.params.distanceBetweenRailHoles,
							firstRailHoleDistance: rails.params.firstRailHoleDistance
						})
					}
				}

				if (el.parent.type == EquipmentElements.Showcase) {
					el.shapes[0].frontWidth = values.frontWidth;
					el.shapes[0].backWidth = values.backWidth;
					el.shapes[0].depth = values.depth;
					el.shapes[0].begin = { ...element.shapes[0].begin };
				}

				if (el.parent.type == EquipmentElements.SideWalls) {
					const sidesWallInfo: { left: SideWallInfo, right: SideWallInfo } = TrapezeBaseGenerator.getSidesWallsInfo(element, el.shapes[1].width);

					el.shapes[0].update({
						begin: sidesWallInfo.left.begin,
						depth: sidesWallInfo.left.depth,
						angle: sidesWallInfo.left.angle,
						empty: sidesWallInfo.left.empty
					});
					el.shapes[1].update({
						begin: sidesWallInfo.right.begin,
						depth: sidesWallInfo.right.depth,
						angle: sidesWallInfo.right.angle,
						empty: sidesWallInfo.right.empty
					});
				}

				if (el.parent.type == EquipmentElements.Shelf) {
					const shape: Figure = el.shapes[0],
						baseShape: Trapeze = element.shapes[0] as Trapeze,
						trapeze: Trapeze = new Trapeze({
							begin: { x: baseShape.begin.x, y: shape.begin.y, z: baseShape.begin.z },
							frontWidth: baseShape.frontWidth,
							backWidth: baseShape.backWidth,
							depth: baseShape.depth,
							height: shape.height,
							empty: {
								front: false,
								top: true
							}
						});

					let frontWidth: number = Utils.round(
						trapeze.backWidth + (trapeze.frontWidth - trapeze.backWidth) * shape.depth / trapeze.depth
					);

					if (trapeze.frontWidth > trapeze.backWidth) {
						trapeze.begin.x = Utils.round(
							trapeze.begin.x + (trapeze.frontWidth - frontWidth) / 2
						);
					}

					trapeze.update({ frontWidth, depth: shape.depth });
					el.shapes = [trapeze];
				}

				if (el.parent.is(EquipmentElements.HangBar)) {
					if (el.shapes[0].begin.x < el.depend.shapes[0].begin.x) {
						el.shapes[0].begin.x = el.depend.shapes[0].begin.x;
					}
					if (el.shapes[0].width > el.depend.shapes[0].width) {
						el.shapes[0].width = el.depend.shapes[0].width;
					}
				}
			});

		equipment.allElements
			.filter((el: Element) => el.depend == element)
			.filter((el: Element) => el.parent.is(EquipmentElements.Box, EquipmentElements.SlicedBox, EquipmentElements.Partition))
			.forEach((el: Element) => {
				ElementGenerator.updateExtensions(el, { availability: values.availability });
			});

		return equipment;
	};

	protected getShapes(): Array<Figure> {
		const trapeze: Trapeze = new Trapeze({
			begin: this.begin,
			frontWidth: 0.6,
			backWidth: 0.4,
			depth: 0.3,
			height: 0.1,
			empty: {
				front: false,
				top: true
			}
		});
		return [trapeze];
	}

	protected getExtensions(): Array<Extension<any>> {
		return TrapezeBaseGenerator.getExtensions().map((extension: any) => {
			const { type, active, ...params } = extension;
			return new Extension<any>(type, active, params);
		});
	}
}

abstract class WallGenerator extends ElementGenerator {

	public constructor(
		protected equipment: EquipmentEditable,
		protected parentElement: EquipmentElement,
		protected dependElement: Element = null,
		protected point?: Point
	) {
		super(equipment, parentElement, dependElement, point);
		this.dependElement = this.equipment.bases.find((i: Element) => i.shapes[0].isPointOn(this.point.x));
	}
}

class BackWallGenerator extends WallGenerator {

	static getOptions(wall: Element, equipment: EquipmentEditable): { [key: string]: Options } {
		const rails: Extension<RailsParams> = wall.getExtension<RailsParams>(ExtensionType.Rails);

		return {
			height: {
				...DEFAULT_OPTION,
				value: wall.shapes[0].height,
				valueChange() {
					if (this.value < MIN_DIMENSION_VALUE) {
						this.valid = false;
						this.error = 'UNABLE_VALUE';
					} else {
						this.valid = true;
						this.error = '';
					}
					this.dirty = this.value != wall.shapes[0].height;
				}
			},
			depth: {
				...DEFAULT_OPTION,
				value: wall.shapes[0].depth,
				valueChange() {
					if (this.value < MIN_DIMENSION_VALUE || this.value > wall.depend.shapes[0].depth) {
						this.valid = false;
						this.error = 'UNABLE_VALUE';
					} else {
						this.valid = true;
						this.error = '';
					}
					this.dirty = this.value != wall.shapes[0].depth;
				}
			},
			rails: {
				...DEFAULT_OPTION,
				value: rails?.active,
				valueChange() {
					this.dirty = this.value != rails?.active;
				}
			},
			railHolesHeight: {
				...DEFAULT_OPTION,
				value: rails?.params.railHolesHeight,
				valueChange() {
					if (this.value < MIN_DIMENSION_VALUE) {
						this.valid = false;
						this.error = 'UNABLE_VALUE';
					} else {
						this.valid = true;
						this.error = '';
					}
					this.dirty = this.value != rails?.params.railHolesHeight;
				}
			},
			railHolesWidth: {
				...DEFAULT_OPTION,
				value: rails?.params.railHolesWidth,
				valueChange() {
					if (this.value < MIN_DIMENSION_VALUE) {
						this.valid = false;
						this.error = 'UNABLE_VALUE';
					} else {
						this.valid = true;
						this.error = '';
					}
					this.dirty = this.value != rails?.params.railHolesWidth;
				}
			},
			distanceBetweenRailHoles: {
				...DEFAULT_OPTION,
				value: rails?.params.distanceBetweenRailHoles,
				valueChange() {
					if (this.value < MIN_DIMENSION_VALUE) {
						this.valid = false;
						this.error = 'UNABLE_VALUE';
					} else {
						this.valid = true;
						this.error = '';
					}
					this.dirty = this.value != rails?.params.distanceBetweenRailHoles;
				}
			},
			firstRailHoleDistance: {
				...DEFAULT_OPTION,
				value: rails?.params.firstRailHoleDistance,
				valueChange() {
					if (this.value < MIN_DIMENSION_VALUE) {
						this.valid = false;
						this.error = 'UNABLE_VALUE';
					} else {
						this.valid = true;
						this.error = '';
					}
					this.dirty = this.value != rails?.params.firstRailHoleDistance;
				}
			}
		}
	}

	static getAnchorPoints(element: Element): Array<number> {
		const rails: Extension<RailsParams> = element.getExtension<RailsParams>(ExtensionType.Rails);

		if (rails?.active) {
			const points: Array<number> = [],
				height: number = element.Height,
				step: number = Utils.round(rails.params.railHolesHeight + rails.params.distanceBetweenRailHoles);

			let distance: number = Utils.round(height - rails.params.firstRailHoleDistance),
				railHole: number = 0;

			while (distance >= rails.params.railHolesHeight) {
				points.push(Utils.round(element.shapes[0].begin.y + rails.params.firstRailHoleDistance + railHole * step));
				distance = Utils.round(distance - step);
				railHole++;
			}

			return points;
		} else {
			return [];
		}
	}

	static getExtensions(): Array<any> {
		return [
			{
				type: ExtensionType.Rails,
				active: false,
				railHolesHeight: 0.03,
				railHolesWidth: 0.02,
				distanceBetweenRailHoles: 0.02,
				firstRailHoleDistance: 0.05
			}
		];
	}

	static updateElement(element: Element, equipment: EquipmentEditable, values: Partial<ElementValues>): EquipmentEditable {
		const zDelta: number = values.depth ? values.depth - element.Depth : 0;
		element.shapes[0].update(values);
		ElementGenerator.updateExtensions(element, values);

		equipment.allElements
			.filter((el: Element) => el.getDepends().includes(element))
			.forEach((el: Element) => {
				if (el.parent.is(...SHELVES, EquipmentElements.HangBar) && element.isExtensionActive(ExtensionType.Rails)) {
					const anchorPoint: number = Utils.getNearestPoint(
						elementsGenerator[EquipmentElements.BackWall].getAnchorPoints(element),
						el.shapes[0].getBoundingY().min
					);
					const base: Element = el.getDepends().find((depend: Element) => !depend.depend);

					if (el.parent.is(EquipmentElements.HangBar)) {
						el.shapes.forEach((shape: Cylinder) => {
							shape.beginSetter = {
								x: el.depend.shapes[0].begin.x,
								y: Utils.round(anchorPoint + shape.diameter / 2),
								z: shape.begin.z
							};
							shape.width = element.Width;
						});

						equipment.allElements
							.filter((e: Element) => e.depend == el)
							.forEach((e: Element) => {
								e.shapes.forEach((shape: Figure) => {
									shape.beginSetter = {
										...shape.begin,
										y: Utils.round(el.shapes[0].begin.y - shape.height / 2)
									};
								});
							});
					} else {
						el.shapes.forEach((shape: Rectangle) => {
							shape.beginSetter = { x: base.shapes[0].begin.x, y: anchorPoint, z: shape.begin.z };
							shape.width = element.Width;
						});

						equipment.allElements
							.filter((e: Element) => e.depend == el)
							.forEach((e: Element) => {
								e.shapes.forEach((shape: Figure) => {
									shape.beginSetter = { ...shape.begin, y: el.getBoundingY().max };
								});
							});
					}
				}
				el.shapes.forEach((shape: Figure) => {
					shape.beginSetter = { ...shape.begin, z: Utils.round(shape.begin.z + zDelta) };
					if (el.parent.is(EquipmentElements.AngleShelf)) {
						(shape as Rectangle).updateRotationPoint(equipment, el);
					}
				});
			});

		return equipment;
	}

	protected getShapes(): Array<Figure> {
		if (!this.dependElement) return;

		const baseGenerator = elementsGenerator[this.dependElement.parent.type as EquipmentBases],
			{ begin, width } = baseGenerator.getBackWallInfo(this.dependElement),

			rect = new Rectangle({
				begin: begin,
				height: UnitConverter.convertAndRoundToSaveValue(1.8),
				depth: UnitConverter.convertAndRoundToSaveValue(0.02),
				width: width,
				empty: {
					front: true,
					top: false
				}
			});

		return [rect];
	}

	protected getExtensions(): Array<Extension<any>> {
		return BackWallGenerator.getExtensions().map((extension: any) => {
			const { type, active, ...params } = extension;
			return new Extension<any>(type, active, params);
		});
	}
}

class CentralPillarGenerator extends WallGenerator {

	static getOptions(pillar: Element, equipment: EquipmentEditable): { [key: string]: Options } {
		const rails: Extension<RailsParams> = pillar.getExtension<RailsParams>(ExtensionType.Rails);

		return {
			length: {
				...DEFAULT_OPTION,
				value: pillar.shapes[0].width,
				valueChange() {
					if (this.value < MIN_DIMENSION_VALUE) {
						this.valid = false;
						this.error = 'UNABLE_VALUE';
					} else if (this.value > pillar.depend.shapes[0].width) {
						this.valid = false;
						this.error = 'PILLAR_WIDTH_WARNING';
					} else {
						this.valid = true;
						this.error = '';
					}
					this.dirty = this.value != pillar.shapes[0].width;
				}
			},
			height: {
				...DEFAULT_OPTION,
				value: pillar.shapes[0].height,
				valueChange() {
					if (this.value < MIN_DIMENSION_VALUE) {
						this.valid = false;
						this.error = 'UNABLE_VALUE';
					} else {
						this.valid = true;
						this.error = '';
					}
					this.dirty = this.value != pillar.shapes[0].height;
				}
			},
			depth: {
				...DEFAULT_OPTION,
				value: pillar.shapes[0].depth,
				valueChange() {
					if (this.value < MIN_DIMENSION_VALUE || this.value > pillar.depend.shapes[0].depth) {
						this.valid = false;
						this.error = 'UNABLE_VALUE';
					} else {
						this.valid = true;
						this.error = '';
					}
					this.dirty = this.value != pillar.shapes[0].depth;
				}
			},
			rails: {
				...DEFAULT_OPTION,
				value: rails?.active,
				valueChange() {
					this.dirty = this.value != rails?.active;
				}
			},
			railHolesHeight: {
				...DEFAULT_OPTION,
				value: rails?.params.railHolesHeight,
				valueChange() {
					if (this.value < MIN_DIMENSION_VALUE) {
						this.valid = false;
						this.error = 'UNABLE_VALUE';
					} else {
						this.valid = true;
						this.error = '';
					}
					this.dirty = this.value != rails?.params.railHolesHeight;
				}
			},
			railHolesWidth: {
				...DEFAULT_OPTION,
				value: rails?.params.railHolesWidth,
				valueChange() {
					if (this.value < MIN_DIMENSION_VALUE) {
						this.valid = false;
						this.error = 'UNABLE_VALUE';
					} else {
						this.valid = true;
						this.error = '';
					}
					this.dirty = this.value != rails?.params.railHolesWidth;
				}
			},
			distanceBetweenRailHoles: {
				...DEFAULT_OPTION,
				value: rails?.params.distanceBetweenRailHoles,
				valueChange() {
					if (this.value < MIN_DIMENSION_VALUE) {
						this.valid = false;
						this.error = 'UNABLE_VALUE';
					} else {
						this.valid = true;
						this.error = '';
					}
					this.dirty = this.value != rails?.params.distanceBetweenRailHoles;
				}
			},
			firstRailHoleDistance: {
				...DEFAULT_OPTION,
				value: rails?.params.firstRailHoleDistance,
				valueChange() {
					if (this.value < MIN_DIMENSION_VALUE) {
						this.valid = false;
						this.error = 'UNABLE_VALUE';
					} else {
						this.valid = true;
						this.error = '';
					}
					this.dirty = this.value != rails?.params.firstRailHoleDistance;
				}
			}
		}
	}

	static getAnchorPoints(element: Element): Array<number> {
		const rails: Extension<RailsParams> = element.getExtension<RailsParams>(ExtensionType.Rails);

		if (rails?.active) {
			const points: Array<number> = [],
				height: number = element.Height,
				step: number = Utils.round(rails.params.railHolesHeight + rails.params.distanceBetweenRailHoles);

			let distance: number = Utils.round(height - rails.params.firstRailHoleDistance),
				railHole: number = 0;

			while (distance >= rails.params.railHolesHeight) {
				points.push(Utils.round(element.shapes[0].begin.y + rails.params.firstRailHoleDistance + railHole * step));
				distance = Utils.round(distance - step);
				railHole++;
			}

			return points;
		} else {
			return [];
		}
	}

	static getExtensions(): Array<any> {
		return [
			{
				type: ExtensionType.Rails,
				active: false,
				railHolesHeight: 0.03,
				railHolesWidth: 0.02,
				distanceBetweenRailHoles: 0.02,
				firstRailHoleDistance: 0.05
			}
		];
	}

	static updateElement(element: Element, equipment: EquipmentEditable, values: Partial<ElementValues>): EquipmentEditable {
		element.shapes[0].update(values);
		ElementGenerator.updateExtensions(element, values);

		if (values.width && element.getBoundingX().max > element.depend.getBoundingX().max) {
			const newPosition: number = Utils.round(element.depend.getBoundingX().max - element.shapes[0].width);
			const offset: number = Utils.round(element.getBoundingX().min - newPosition);

			element.shapes[0].beginSetter = {
				...element.shapes[0].begin,
				x: newPosition
			};

			equipment.allElements
				.filter((el: Element) => el.getDepends().includes(element))
				.forEach((el: Element) => {
					el.shapes[0].beginSetter = {
						...el.shapes[0].begin,
						x: Utils.round(el.shapes[0].begin.x - offset)
					};
				});
		}

		if (values.depth) {
			element.shapes[0].beginSetter = {
				...element.shapes[0].begin,
				z: Utils.round((element.depend.shapes[0].depth - element.shapes[0].depth) / 2)
			};
		}

		const pillarCenter = Utils.round(element.getBoundingX().min + element.Width / 2);

		equipment.allElements
			.filter((el: Element) => el.depend == element)
			.forEach((el: Element) => {
				if (el.Width < element.Width) el.shapes[0].update({ width: element.Width });

				let newPosition: number = pillarCenter - el.Width / 2;

				if (newPosition < element.depend.getBoundingX().min) newPosition = element.depend.getBoundingX().min;
				if (Utils.round(newPosition + el.Width) > element.depend.getBoundingX().max) {
					newPosition = Utils.round(element.depend.getBoundingX().max - el.Width);
				}

				const diff: number = Utils.round(newPosition - el.getBoundingX().min);

				if (diff) {
					el.shapes.forEach((shape: Figure) => {
						shape.drag(diff, 0, 0);

						equipment.allElements
							.filter((e: Element) => !e.parent.is(EquipmentElements.Hole))
							.filter((e: Element) => e.getDepends().includes(el))
							.forEach((e: Element) => {
								e.shapes.forEach((sh: Figure) => {
									sh.drag(diff, 0, 0);
								});
							});
					});
				}

				if (el.parent.is(EquipmentElements.Shelf) && element.isExtensionActive(ExtensionType.Rails)) {
					const anchorPoint: number = Utils.getNearestPoint(
						elementsGenerator[EquipmentElements.CentralPillar].getAnchorPoints(element),
						el.shapes[0].getBoundingY().min
					);

					el.shapes.forEach((shape: Rectangle) => {
						shape.beginSetter = { ...shape.begin, y: anchorPoint };
					});

					equipment.allElements
						.filter((e: Element) => e.depend == el)
						.forEach((e: Element) => {
							e.shapes.forEach((shape: Figure) => {
								shape.beginSetter = { ...shape.begin, y: el.getBoundingY().max };
							});
						});
				}
			});

		return equipment;
	}

	static updateByParent(element: Element, equipment: EquipmentEditable): void {
		if (!element.depend) return;

		const shape: Figure = element.shapes[0];
		const parentShape: Figure = element.depend.shapes[0];

		if (parentShape.width < shape.width) shape.update({ width: parentShape.width });
		if (parentShape.depth < shape.depth) shape.update({ depth: parentShape.depth });

		const diff: number = Utils.round(parentShape.getBoundingX().max - shape.getBoundingX().max);

		if (diff < 0) {
			shape.beginSetter = { ...shape.begin, x: Utils.round(shape.begin.x + diff) };
		}

		const newZ: number = Utils.round((parentShape.depth - shape.depth) / 2);

		if (shape.begin.z != newZ) {
			const zDelta: number = Utils.round(newZ - shape.begin.z);
			shape.beginSetter = { ...shape.begin, z: newZ };

			equipment.allElements
				.filter((el: Element) => el.getDepends().includes(element))
				.forEach((el: Element) => {
					el.shapes.forEach((shape: Figure) => shape.begin.z = Utils.round(shape.begin.z + zDelta));
				});
		}
	}

	protected getShapes(): Array<Figure> {
		if (!this.dependElement) return;

		const baseGenerator = elementsGenerator[this.dependElement.parent.type as EquipmentBases],
			{ begin, width } = baseGenerator.getCentralPillarInfo(this.dependElement),
			rect = new Rectangle({
				begin: {
					x: Utils.round(begin.x - width / 2),
					y: begin.y,
					z: Utils.round(begin.z - 0.05)
				},
				height: UnitConverter.convertAndRoundToSaveValue(1.3),
				depth: UnitConverter.convertAndRoundToSaveValue(0.1),
				width: width,
				empty: {
					front: true,
					top: false
				}
			});

		return [rect];
	}

	protected getExtensions(): Array<Extension<any>> {
		return CentralPillarGenerator.getExtensions().map((extension: any) => {
			const { type, active, ...params } = extension;
			return new Extension<any>(type, active, params);
		});
	}
}

class HookWallGenerator extends WallGenerator {

	static getOptions(wall: Element, equipment: EquipmentEditable): { [key: string]: Options } {
		return {
			height: {
				...DEFAULT_OPTION,
				value: wall.shapes[0].height,
				valueChange() {
					if (this.value < MIN_DIMENSION_VALUE) {
						this.valid = false;
						this.error = 'UNABLE_VALUE';
					} else {
						this.valid = true;
						this.error = '';
					}
					this.dirty = this.value != wall.shapes[0].height;
				}
			},
			depth: {
				...DEFAULT_OPTION,
				value: wall.shapes[0].depth,
				valueChange() {
					if (this.value < MIN_DIMENSION_VALUE || this.value > wall.depend.shapes[0].depth) {
						this.valid = false;
						this.error = 'UNABLE_VALUE';
					} else {
						this.valid = true;
						this.error = '';
					}
					this.dirty = this.value != wall.shapes[0].depth;
				}
			},
			hooksAutolayout: {
				...DEFAULT_OPTION,
				value: false,
				valueChange() {
					this.dirty = this.value != (wall.shapes[0] as Rectangle).hooksAutolayout;
				}
			},
			distanceBetweenHooks: {
				...DEFAULT_OPTION,
				value: (wall.shapes[0] as Rectangle).distanceBetweenHooks,
				valueChange() {
					if (this.value < MIN_DIMENSION_VALUE || this.value > wall.shapes[0].width) {
						this.valid = false;
						this.error = 'UNABLE_VALUE';
					} else {
						this.valid = true;
						this.error = '';
					}
					this.dirty = this.value != (wall.shapes[0] as Rectangle).distanceBetweenHooks;
				}
			},
			distanceBetweenRowsHooks: {
				...DEFAULT_OPTION,
				value: (wall.shapes[0] as Rectangle).distanceBetweenRowsHooks,
				valueChange() {
					if (this.value < MIN_DIMENSION_VALUE || this.value > wall.shapes[0].height) {
						this.valid = false;
						this.error = 'UNABLE_VALUE';
					} else {
						this.valid = true;
						this.error = '';
					}
					this.dirty = this.value != (wall.shapes[0] as Rectangle).distanceBetweenRowsHooks;
				}
			},
			firstHookTopDistance: {
				...DEFAULT_OPTION,
				value: (wall.shapes[0] as Rectangle).firstHookTopDistance,
				valueChange() {
					if (this.value < MIN_DIMENSION_VALUE || this.value > wall.shapes[0].height) {
						this.valid = false;
						this.error = 'UNABLE_VALUE';
					} else {
						this.valid = true;
						this.error = '';
					}
					this.dirty = this.value != (wall.shapes[0] as Rectangle).firstHookTopDistance;
				}
			},
			firstHookLeftDistance: {
				...DEFAULT_OPTION,
				value: (wall.shapes[0] as Rectangle).firstHookLeftDistance,
				valueChange() {
					if (this.value < MIN_DIMENSION_VALUE || this.value > wall.shapes[0].width) {
						this.valid = false;
						this.error = 'UNABLE_VALUE';
					} else {
						this.valid = true;
						this.error = '';
					}
					this.dirty = this.value != (wall.shapes[0] as Rectangle).firstHookLeftDistance;
				}
			}
		}
	}

	static updateElement(element: Element, equipment: EquipmentEditable, values: {
		height: number,
		depth: number,
		distanceBetweenHooks?: number,
		distanceBetweenRowsHooks?: number,
		firstHookTopDistance?: number,
		firstHookLeftDistance?: number,
		hooksAutolayout?: boolean
	}) {
		const zDelta: number = values.depth - element.shapes[0].depth;
		element.shapes[0].update(values);

		equipment.allElements.filter(el => el.getDepends().includes(element)).forEach(el => {
			el.shapes.forEach(shape => {
				shape.begin.z += zDelta;
			});
		});

		return equipment;
	}

	protected getShapes() {
		if (!this.dependElement) return;

		const baseGenerator = elementsGenerator[this.dependElement.parent.type as EquipmentBases],
			{ begin, width } = baseGenerator.getBackWallInfo(this.dependElement),
			rect = new Rectangle({
				begin: begin,
				height: UnitConverter.convertAndRoundToSaveValue(1.8),
				depth: UnitConverter.convertAndRoundToSaveValue(0.02),
				width: width,
				empty: {
					front: true,
					top: false
				}
			});

		return [rect];
	}

	protected getExtensions(): Array<Extension<any>> {
		return [];
	}
}

class PerforatedWallGenerator extends WallGenerator {

	static getOptions(wall: Element, equipment: EquipmentEditable): { [key: string]: Options } {
		return {
			height: {
				...DEFAULT_OPTION,
				value: wall.shapes[0].height,
				valueChange() {
					if (this.value < MIN_DIMENSION_VALUE) {
						this.valid = false;
						this.error = 'UNABLE_VALUE';
					} else {
						this.valid = true;
						this.error = '';
					}
					this.dirty = this.value != wall.shapes[0].height;
				}
			},
			depth: {
				...DEFAULT_OPTION,
				value: wall.shapes[0].depth,
				valueChange() {
					if (this.value < MIN_DIMENSION_VALUE || this.value > wall.depend.shapes[0].depth) {
						this.valid = false;
						this.error = 'UNABLE_VALUE';
					} else {
						this.valid = true;
						this.error = '';
					}
					this.dirty = this.value != wall.shapes[0].depth;
				}
			},
			distanceBetweenHoles: {
				...DEFAULT_OPTION,
				value: (wall.shapes[0] as Rectangle).distanceBetweenHoles,
				valueChange() {
					if (this.value < MIN_DIMENSION_VALUE || this.value > wall.shapes[0].width) {
						this.valid = false;
						this.error = 'UNABLE_VALUE';
					} else {
						this.valid = true;
						this.error = '';
					}
					this.dirty = this.value != (wall.shapes[0] as Rectangle).distanceBetweenHoles;
				}
			},
			distanceBetweenRowsHoles: {
				...DEFAULT_OPTION,
				value: (wall.shapes[0] as Rectangle).distanceBetweenRowsHoles,
				valueChange() {
					if (this.value < MIN_DIMENSION_VALUE || this.value > wall.shapes[0].height) {
						this.valid = false;
						this.error = 'UNABLE_VALUE';
					} else {
						this.valid = true;
						this.error = '';
					}
					this.dirty = this.value != (wall.shapes[0] as Rectangle).distanceBetweenRowsHoles;
				}
			},
			firstHoleTopDistance: {
				...DEFAULT_OPTION,
				value: (wall.shapes[0] as Rectangle).firstHoleTopDistance,
				valueChange() {
					if (this.value < MIN_DIMENSION_VALUE || this.value > wall.shapes[0].height) {
						this.valid = false;
						this.error = 'UNABLE_VALUE';
					} else {
						this.valid = true;
						this.error = '';
					}
					this.dirty = this.value != (wall.shapes[0] as Rectangle).firstHoleTopDistance;
				}
			},
			firstHoleLeftDistance: {
				...DEFAULT_OPTION,
				value: (wall.shapes[0] as Rectangle).firstHoleLeftDistance,
				valueChange() {
					if (this.value < MIN_DIMENSION_VALUE || this.value > wall.shapes[0].width) {
						this.valid = false;
						this.error = 'UNABLE_VALUE';
					} else {
						this.valid = true;
						this.error = '';
					}
					this.dirty = this.value != (wall.shapes[0] as Rectangle).firstHoleLeftDistance;
				}
			}
		}
	}

	static updateElement(element: Element, equipment: EquipmentEditable, values: {
		height: number,
		length: number,
		depth: number,
		distanceBetweenHoles: number,
		distanceBetweenRowsHoles: number,
		firstHoleTopDistance: number,
		firstHoleLeftDistance: number,
	}) {
		const zDelta: number = values.depth - element.shapes[0].depth;
		element.shapes[0].update(values);

		equipment.allElements.filter(el => el.getDepends().includes(element)).forEach(el => {
			el.shapes.forEach(shape => {
				shape.begin.z += zDelta;
			});
		});

		return equipment;
	}

	protected getShapes() {
		if (!this.dependElement) return;

		const baseGenerator = elementsGenerator[this.dependElement.parent.type as EquipmentBases],
			{ begin, width } = baseGenerator.getBackWallInfo(this.dependElement),
			rect = new Rectangle({
				begin: begin,
				height: UnitConverter.convertAndRoundToSaveValue(1.8),
				depth: UnitConverter.convertAndRoundToSaveValue(0.02),
				width: width,
				empty: {
					front: true,
					top: false
				}
			});

		return [rect];
	}

	protected getExtensions(): Array<Extension<any>> {
		return [];
	}
}

class SidesWallGenerator extends WallGenerator {

	static getOptions(sideWalls: Element, equipment: EquipmentEditable): { [key: string]: Options } {
		const maxWallWidth: number = Utils.round(sideWalls.depend.shapes[0].width / 2);

		return {
			leftWallWidth: {
				...DEFAULT_OPTION,
				value: sideWalls.shapes[0].width,
				valueChange() {
					if (this.value < MIN_DIMENSION_VALUE || this.value > maxWallWidth) {
						this.valid = false;
						this.error = 'UNABLE_VALUE';
					} else {
						this.valid = true;
						this.error = '';
					}
					this.dirty = this.value != sideWalls.shapes[0].width;
				}
			},
			leftWallHeight: {
				...DEFAULT_OPTION,
				value: sideWalls.shapes[0].height,
				valueChange() {
					if (this.value < MIN_DIMENSION_VALUE) {
						this.valid = false;
						this.error = 'UNABLE_VALUE';
					} else {
						this.valid = true;
						this.error = '';
					}
					this.dirty = this.value != sideWalls.shapes[0].height;
				}
			},
			rightWallWidth: {
				...DEFAULT_OPTION,
				value: sideWalls.shapes[1].width,
				valueChange() {
					if (this.value < MIN_DIMENSION_VALUE || this.value > maxWallWidth) {
						this.valid = false;
						this.error = 'UNABLE_VALUE';
					} else {
						this.valid = true;
						this.error = '';
					}
					this.dirty = this.value != sideWalls.shapes[1].width;
				}
			},
			rightWallHeight: {
				...DEFAULT_OPTION,
				value: sideWalls.shapes[1].height,
				valueChange() {
					if (this.value < MIN_DIMENSION_VALUE) {
						this.valid = false;
						this.error = 'UNABLE_VALUE';
					} else {
						this.valid = true;
						this.error = '';
					}
					this.dirty = this.value != sideWalls.shapes[1].height;
				}
			}
		}
	}

	static updateElement(element: Element, equipment: EquipmentEditable, values: {
		leftWallWidth: number,
		leftWallHeight: number,
		rightWallWidth: number,
		rightWallHeight: number
	}) {
		const baseGenerator = elementsGenerator[element.depend.parent.type as EquipmentBases],
			{ left, right } = baseGenerator.getSidesWallsInfo(element.depend, values.rightWallWidth);

		element.shapes[0].update({
			begin: left.begin,
			width: values.leftWallWidth,
			height: values.leftWallHeight
		});

		element.shapes[1].update({
			begin: right.begin,
			width: values.rightWallWidth,
			height: values.rightWallHeight
		});

		return equipment;
	}

	protected getShapes() {
		if (!this.dependElement) return;

		const baseGenerator = elementsGenerator[this.dependElement.parent.type as EquipmentBases],
			backWall = this.equipment.allElements
				.filter(el => el.parent.is(EquipmentElements.BackWall, EquipmentElements.HookWall, EquipmentElements.PerforatedWall))
				.find(el => el.getDepends().includes(this.dependElement)),

			height = backWall ? (backWall.shapes[0] as Rectangle).height : 1.8,
			{ left, right } = baseGenerator.getSidesWallsInfo(this.dependElement, 0.02),

			leftRect = new Rectangle({
				begin: left.begin,
				height: height,
				depth: left.depth,
				angle: left.angle,
				width: UnitConverter.convertAndRoundToSaveValue(0.02),
				empty: left.empty
			}),
			rightRect = new Rectangle({
				begin: right.begin,
				height: height,
				depth: right.depth,
				angle: right.angle,
				width: UnitConverter.convertAndRoundToSaveValue(0.02),
				empty: right.empty
			});

		return [leftRect, rightRect];
	}

	protected getExtensions(): Array<Extension<any>> {
		return [];
	}
}

class ShowcaseGenerator extends WallGenerator {

	static getOptions(showcase: Element, equipment: EquipmentEditable): { [key: string]: Options } {
		return {
			height: {
				...DEFAULT_OPTION,
				value: showcase.shapes[0].height,
				valueChange() {
					if (this.value < MIN_DIMENSION_VALUE) {
						this.valid = false;
						this.error = 'UNABLE_VALUE';
					} else {
						this.valid = true;
						this.error = '';
					}
					this.dirty = this.value != showcase.shapes[0].height;
				}
			}
		}
	}

	static updateElement(element: Element, equipment: EquipmentEditable, values: {
		height: number,
	}) {
		element.shapes[0].update(values);

		return equipment;
	}

	protected getShapes() {
		const baseGenerator = elementsGenerator[this.dependElement.parent.type as EquipmentBases];

		return baseGenerator.getShowCaseShapes(this.dependElement, UnitConverter.convertAndRoundToSaveValue(0.5));
	}

	protected getExtensions(): Array<Extension<any>> {
		return [];
	}
}

class ShelfGenerator extends ElementGenerator {

	static getOptions(shelf: Element, equipment: EquipmentEditable): { [key: string]: Options } {
		const availability: boolean = !(shelf.hasExtension(ExtensionType.Availability) && !shelf.isExtensionActive(ExtensionType.Availability));
		const rounding: Extension<RoundingParams> = shelf.getExtension(ExtensionType.Rounding);
		const commonOptions = {
			height: {
				...DEFAULT_OPTION,
				value: shelf.shapes[0].height,
				valueChange() {
					if (this.value < MIN_DIMENSION_VALUE || this.value > shelf.depend.shapes[0].height) {
						this.valid = false;
						this.error = 'UNABLE_VALUE';
					} else {
						this.valid = true;
						this.error = '';
					}
					this.dirty = this.value != shelf.shapes[0].height;
				}
			},
			depth: {
				...DEFAULT_OPTION,
				value: shelf.shapes[0].depth,
				valueChange() {
					if (this.value < MIN_DIMENSION_VALUE) {
						this.valid = false;
						this.error = 'UNABLE_VALUE';
					} else {
						this.valid = true;
						this.error = '';
					}
					this.dirty = this.value != shelf.shapes[0].depth;
				}
			},
			availability: {
				...DEFAULT_OPTION,
				value: availability,
				valueChange() {
					this.dirty = this.value != availability;
				}
			}
		};

		if (shelf.shapes[0].type == 'trapeze') {
			return commonOptions;
		} else {
			const disabled: boolean = shelf.depend.isExtensionActive(ExtensionType.Rails)
				&& !shelf.depend.parent.is(EquipmentElements.CentralPillar);
			const options: { [key: string]: Options } = {
				length: {
					dirty: false,
					disabled: disabled,
					disabledMessage: disabled ? 'EQUIPMENT_EDIT.CONFIGURATION_PARAMS.SHELF_WIDTH_RAILS_HINT' : '',
					error: '',
					valid: true,
					value: shelf.shapes[0].width,
					valueChange() {
						if (shelf.depend.parent.is(EquipmentElements.CentralPillar)) {
							if (this.value < shelf.depend.shapes[0].width) {
								this.valid = false;
								this.error = 'UNABLE_VALUE';
							} else if (this.value > shelf.depend.depend.shapes[0].width) {
								this.valid = false;
								this.error = 'SHELF_WIDTH_WARNING';
							} else {
								this.valid = true;
								this.error = '';
							}
						} else {
							if (this.value < MIN_DIMENSION_VALUE) {
								this.valid = false;
								this.error = 'UNABLE_VALUE';
							} else if (this.value > shelf.depend.shapes[0].width) {
								this.valid = false;
								this.error = 'SHELF_WIDTH_WARNING';
							} else {
								this.valid = true;
								this.error = '';
							}
						}

						this.dirty = this.value != shelf.shapes[0].width;
					}
				},
				height: commonOptions.height,
				depth: commonOptions.depth,
				rounding: {
					...DEFAULT_OPTION,
					value: rounding?.active,
					valueChange() {
						this.dirty = this.value != rounding?.active;
					}
				},
				leftSideRadius: {
					...DEFAULT_OPTION,
					value: rounding?.params.leftSideRadius,
					valueChange() {
						if (this.value < 0 || this.value > 50) {
							this.valid = false;
							this.error = 'UNABLE_VALUE';
						} else {
							this.valid = true;
							this.error = '';
						}
						this.dirty = this.value != rounding?.params.leftSideRadius;
					}
				},
				rightSideRadius: {
					...DEFAULT_OPTION,
					value: rounding?.params.rightSideRadius,
					valueChange() {
						if (this.value < 0 || this.value > 50) {
							this.valid = false;
							this.error = 'UNABLE_VALUE';
						} else {
							this.valid = true;
							this.error = '';
						}
						this.dirty = this.value != rounding?.params.rightSideRadius;
					}
				},
				availability: commonOptions.availability
			};

			if (!shelf.depend.parent.is(EquipmentElements.CentralPillar)) {
				delete options.rounding;
				delete options.leftSideRadius;
				delete options.rightSideRadius;
			}

			return options;
		}
	}

	static getExtensions(): Array<any> {
		return [
			{
				type: ExtensionType.Rounding,
				active: false,
				leftSideRadius: 0,
				rightSideRadius: 0
			},
			{
				type: ExtensionType.Availability,
				active: true
			}
		];
	};

	static updateElement(element: Element, equipment: EquipmentEditable, values: Partial<ElementValues>): EquipmentEditable {
		const shape: Trapeze = element.shapes[0] as Trapeze;

		if (!element.depend.parent.is(EquipmentElements.CentralPillar)) {
			delete values.rounding;
			delete values.leftSideRadius;
			delete values.rightSideRadius;
		}

		if (equipment.bases.filter((i: Element) => i.parent.type.includes(EquipmentElements.BaseTrapeze))) {
			let frontWidth: number = shape.backWidth + (shape.frontWidth - shape.backWidth) * values.depth / shape.depth;
			if (shape.frontWidth > shape.backWidth) {
				let xDelta: number = (shape.frontWidth - frontWidth) / 2;
				shape.begin.x += xDelta;
			}
			shape.update({ frontWidth });
		}
		shape.update(values);
		ElementGenerator.updateExtensions(element, values);

		if (element.depend.parent.is(EquipmentElements.CentralPillar)) {
			const base: Element = element.depend.depend;
			const pillarCenterX = Utils.round(element.depend.getBoundingX().min + element.depend.Width / 2);
			const pillarCenterZ = Utils.round(element.depend.getBoundingZ().min + element.depend.Depth / 2);

			let newPositionX: number = Utils.round(pillarCenterX - element.Width / 2);
			const newPositionZ: number = Utils.round(pillarCenterZ - element.Depth / 2);

			if (newPositionX < base.getBoundingX().min) newPositionX = base.getBoundingX().min;
			if (Utils.round(newPositionX + element.Width) > base.getBoundingX().max) {
				newPositionX = Utils.round(base.getBoundingX().max - element.Width);
			}

			const diffX: number = Utils.round(newPositionX - element.getBoundingX().min);
			const diffZ: number = Utils.round(newPositionZ - element.getBoundingZ().min);

			if (diffX || diffZ) {
				element.shapes.forEach((shape: Figure) => {
					shape.drag(diffX, 0, diffZ);

					equipment.allElements
						.filter((el: Element) => !el.parent.is(EquipmentElements.Hole))
						.filter((el: Element) => el.getDepends().includes(element))
						.forEach((el: Element) => {
							el.shapes.forEach((sh: Figure) => {
								sh.drag(diffX, 0, diffZ);
							});
						});
				});
			}
		}

		equipment.allElements
			.filter((el: Element) => el.depend == element)
			.filter((el: Element) => el.parent.is(EquipmentElements.Box, EquipmentElements.SlicedBox, EquipmentElements.Partition))
			.forEach((el: Element) => {
				el.shapes[0].beginSetter = {
					x: el.shapes[0].begin.x,
					y: Utils.round(element.shapes[0].begin.y + element.shapes[0].height),
					z: el.shapes[0].begin.z
				};
				ElementGenerator.updateExtensions(el, { availability: values.availability });
			});

		return equipment;
	}

	protected getShapes(): Array<Figure> {
		const base: Element = this.equipment.bases.find((i: Element) => i.shapes[0].isPointOn(this.point.x));

		switch (base.parent.type) {
			case EquipmentElements.BaseRectangle: return this.getShelfRectangle();
			case EquipmentElements.BaseTrapeze: return this.getShelfTrapeze();
		}
		return [];
	}

	private getShelfRectangle(): Array<Rectangle> {
		const x: number = this.point.x,
			y: number = Utils.round(this.equipment.Height - this.point.y),
			base: Element = this.equipment.bases.find((i: Element) => i.shapes[0].isPointOn(this.point.x));

		let elements: Array<Element> = this.equipment.allElements
				.filter((el: Element) => el != this.dependElement && el.parent.type != EquipmentElements.SideWalls),
			shapes: Array<Bounding> = [].concat(...elements.map((element: Element) => element.shapes))
				.filter((shape: Figure) => shape.isPointOn(x, y, null))
				.map((shape: Figure) => shape.getBoundingX());

		const baseBoundingX: Bounding = base.getBoundingX();

		if (shapes.length) {
			let distances: Array<Array<number>> = [];

			shapes = shapes.sort((a: Bounding, b: Bounding) => a.min - b.min);
			shapes.forEach((shape: Bounding, idx: number) => {
				if (idx == 0) distances.push([baseBoundingX.min, shape.min]);
				if (idx == shapes.length - 1) {
					distances.push([shape.max, baseBoundingX.max]);
					return;
				}
				distances.push([shape.max, shapes[idx + 1].min]);
			});

			const distance: Array<number> = distances.find((d: Array<number>) => x >= d[0] && x <= d[1]);

			if (distance) {
				baseBoundingX.min = distance[0];
				baseBoundingX.max = distance[1];
			}
		}

		const baseBoundingZ: Bounding = base.getBoundingZ();

		if ([EquipmentElements.BackWall].includes(this.dependElement.parent.type)) {
			baseBoundingZ.min = this.dependElement.getBoundingZ().max;
		}

		let shelfDepth: number = Utils.round(baseBoundingZ.max - baseBoundingZ.min);

		if (shelfDepth <= 0) shelfDepth = UnitConverter.convertAndRoundToSaveValue(0.4);

		const begin: Point = {
				x: baseBoundingX.min,
				y: y,
				z: baseBoundingZ.min
			},
			rect: Rectangle = new Rectangle({
				begin,
				width: Utils.round(baseBoundingX.max - baseBoundingX.min),
				depth: shelfDepth,
				height: UnitConverter.convertAndRoundToSaveValue(0.01),
				empty: {
					front: false,
					top: true
				}
			});

		return [rect];
	}

	private getShelfTrapeze(): Array<Trapeze> {
		const y: number = Utils.round(this.equipment.Height - this.point.y),
			base: Element = this.equipment.bases.find(i => i.shapes[0].isPointOn(this.point.x)),
			baseShape: Trapeze = base.shapes[0] as Trapeze,
			trapeze: Trapeze = new Trapeze({
				begin: {
					x: baseShape.begin.x,
					y: y,
					z: baseShape.begin.z
				},
				frontWidth: baseShape.frontWidth,
				backWidth: baseShape.backWidth,
				depth: baseShape.depth,
				height: UnitConverter.convertAndRoundToSaveValue(0.01),
				empty: {
					front: false,
					top: true
				}
			});

		return [trapeze];
	}

	protected getExtensions(): Array<Extension<any>> {
		return ShelfGenerator.getExtensions().map((extension: any) => {
			const { type, active, ...params } = extension;
			return new Extension<any>(type, active, params);
		});
	}
}

class AngleShelfGenerator extends ElementGenerator {

	static getOptions(shelf: Element, equipment: EquipmentEditable): { [key: string]: Options } {
		const availability: boolean = !(shelf.hasExtension(ExtensionType.Availability) && !shelf.isExtensionActive(ExtensionType.Availability));
		const disabled: boolean = shelf.depend.isExtensionActive(ExtensionType.Rails);

		return {
			length: {
				dirty: false,
				disabled: disabled,
				disabledMessage: disabled ? 'EQUIPMENT_EDIT.CONFIGURATION_PARAMS.SHELF_WIDTH_RAILS_HINT' : '',
				error: '',
				valid: true,
				value: shelf.shapes[0].width,
				valueChange() {
					if (this.value < MIN_DIMENSION_VALUE) {
						this.valid = false;
						this.error = 'UNABLE_VALUE';
					} else if (this.value > shelf.depend.shapes[0].width) {
						this.valid = false;
						this.error = 'SHELF_WIDTH_WARNING';
					} else {
						this.valid = true;
						this.error = '';
					}
					this.dirty = this.value != shelf.shapes[0].width;
				}
			},
			height: {
				...DEFAULT_OPTION,
				value: shelf.shapes[0].height,
				valueChange() {
					if (this.value < MIN_DIMENSION_VALUE || this.value > shelf.depend.shapes[0].height) {
						this.valid = false;
						this.error = 'UNABLE_VALUE';
					} else {
						this.valid = true;
						this.error = '';
					}
					this.dirty = this.value != shelf.shapes[0].height;
				}
			},
			depth: {
				...DEFAULT_OPTION,
				value: shelf.shapes[0].depth,
				valueChange() {
					if (this.value < MIN_DIMENSION_VALUE) {
						this.valid = false;
						this.error = 'UNABLE_VALUE';
					} else {
						this.valid = true;
						this.error = '';
					}
					this.dirty = this.value != shelf.shapes[0].depth;
				}
			},
			frontAngle: {
				...DEFAULT_OPTION,
				value: (shelf.shapes[0] as Rectangle).frontAngle,
				valueChange() {
					if (this.value < 1 || this.value > 45) {
						this.valid = false;
						this.error = 'CREATE_EQUIPMENT.ENTER_VALUE_IN_RANGE';
					} else {
						this.valid = true;
						this.error = '';
					}
					this.dirty = this.value != (shelf.shapes[0] as Rectangle).frontAngle;
				}
			},
			availability: {
				...DEFAULT_OPTION,
				value: availability,
				valueChange() {
					this.dirty = this.value != availability;
				}
			}
		};
	}

	static getExtensions(): Array<any> {
		return [
			{
				type: ExtensionType.Availability,
				active: true
			}
		];
	};

	static updateElement(element: Element, equipment: EquipmentEditable, values: Partial<ElementValues>): EquipmentEditable {
		const shape: Rectangle = element.shapes[0] as Rectangle;

		shape.update(values);
		shape.updateRotationPoint(equipment, element);
		ElementGenerator.updateExtensions(element, values);

		equipment.allElements
			.filter((el: Element) => el.depend == element)
			.filter((el: Element) => el.parent.is(EquipmentElements.Box, EquipmentElements.SlicedBox, EquipmentElements.Partition))
			.forEach((el: Element) => {
				el.shapes[0].beginSetter = {
					x: el.shapes[0].begin.x,
					y: Utils.round(element.shapes[0].begin.y + element.shapes[0].height),
					z: el.shapes[0].begin.z
				};
				ElementGenerator.updateExtensions(el, { availability: values.availability });
			});

		return equipment;
	};

	protected getShapes(): Array<Rectangle> {
		const base: Element = this.equipment.bases.find((i: Element) => i.shapes[0].isPointOn(this.point.x));

		if (base.parent.type == EquipmentElements.BaseRectangle) return this.getShelfRectangle();

		return [];
	}

	private getShelfRectangle(): Array<Rectangle> {
		const x: number = this.point.x,
			y: number = Utils.round(this.equipment.Height - this.point.y),
			base: Element = this.equipment.bases.find((i: Element) => i.shapes[0].isPointOn(this.point.x)),

			elements: Array<Element> = this.equipment.allElements
				.filter((el: Element) => el != this.dependElement && el.parent.type != EquipmentElements.SideWalls);

		let shapes: Array<Bounding> = [].concat(...elements.map((element: Element) => element.shapes))
			.filter((shape: Figure) => shape.isPointOn(x, y, null))
			.map((shape: Figure) => shape.getBoundingX());

		const baseBoundingX: Bounding = base.getBoundingX();

		if (shapes.length) {
			let distances: Array<Array<number>> = [];

			shapes = shapes.sort((a: Bounding, b: Bounding) => a.min - b.min);
			shapes.forEach((shape: Bounding, idx: number) => {
				if (idx == 0) {
					distances.push([baseBoundingX.min, shape.min]);
				}
				if (idx == shapes.length - 1) {
					distances.push([shape.max, baseBoundingX.max]);
					return;
				}
				distances.push([shape.max, shapes[idx + 1].min]);
			});

			const distance: Array<number> = distances.find((d: Array<number>) => x >= d[0] && x <= d[1]);

			if (distance) {
				baseBoundingX.min = distance[0];
				baseBoundingX.max = distance[1];
			}
		}

		const baseBoundingZ: Bounding = base.getBoundingZ();

		if ([EquipmentElements.BackWall].includes(this.dependElement.parent.type)) {
			baseBoundingZ.min = this.dependElement.getBoundingZ().max;
		}

		const begin: Point = {
				x: baseBoundingX.min,
				y: y,
				z: baseBoundingZ.min
			},
			rect: Rectangle = new Rectangle({
				begin,
				width: Utils.round(baseBoundingX.max - baseBoundingX.min),
				depth: Utils.round(baseBoundingZ.max - baseBoundingZ.min),
				height: UnitConverter.convertAndRoundToSaveValue(0.01),
				fillColor: '#eaeaea',
				frontAngle: 30,
				rotationPoint: {
					x: begin.z,
					y: Utils.round(begin.y + 0.01)
				},
				empty: {
					front: false,
					top: true
				}
			});

		return [rect];
	}

	protected getExtensions(): Array<Extension<any>> {
		return AngleShelfGenerator.getExtensions().map((extension: any) => {
			const { type, active, ...params } = extension;
			return new Extension<any>(type, active, params);
		});
	}
}

class HangBarGenerator extends ElementGenerator {

	static getOptions(hangBar: Element, equipment: EquipmentEditable): { [key: string]: Options } {
		const availability: boolean = !(hangBar.hasExtension(ExtensionType.Availability) && !hangBar.isExtensionActive(ExtensionType.Availability));
		const disabled: boolean = hangBar.depend.isExtensionActive(ExtensionType.Rails);
		const offset: number = Utils.round(hangBar.shapes[0].begin.z - hangBar.depend.getBoundingZ().max);

		return {
			length: {
				dirty: false,
				disabled: disabled,
				disabledMessage: disabled ? 'EQUIPMENT_EDIT.CONFIGURATION_PARAMS.HANG_BAR_WIDTH_RAILS_HINT' : '',
				error: '',
				valid: true,
				value: hangBar.shapes[0].width,
				valueChange() {
					if (this.value < MIN_DIMENSION_VALUE || this.value > hangBar.depend.shapes[0].width) {
						this.valid = false;
						this.error = 'UNABLE_VALUE';
					} else {
						this.valid = true;
						this.error = '';
					}
					this.dirty = this.value != hangBar.shapes[0].width;
				}
			},
			diameter: {
				...DEFAULT_OPTION,
				value: (hangBar.shapes[0] as Cylinder).diameter,
				valueChange() {
					if (this.value < MIN_DIMENSION_VALUE) {
						this.valid = false;
						this.error = 'UNABLE_VALUE';
					} else {
						this.valid = true;
						this.error = '';
					}
					this.dirty = this.value != (hangBar.shapes[0] as Cylinder).diameter;
				}
			},
			hangBarOffset: {
				...DEFAULT_OPTION,
				value: offset,
				valueChange() {
					if (this.value < MIN_DIMENSION_VALUE) {
						this.valid = false;
						this.error = 'UNABLE_VALUE';
					} else {
						this.valid = true;
						this.error = '';
					}
					this.dirty = this.value != offset;
				}
			},
			availability: {
				...DEFAULT_OPTION,
				value: availability,
				valueChange() {
					this.dirty = this.value != availability;
				}
			}
		}
	}

	static getExtensions(): Array<any> {
		return [
			{
				type: ExtensionType.Availability,
				active: true
			}
		];
	};

	static updateElement(element: Element, equipment: EquipmentEditable, values: Partial<ElementValues>): EquipmentEditable {
		let yDelta: number = 0;

		if (element.depend.isExtensionActive(ExtensionType.Rails) && values.diameter != (element.shapes[0] as Cylinder).diameter) {
			yDelta = Utils.round((values.diameter - (element.shapes[0] as Cylinder).diameter) / 2);
		}

		element.shapes[0].update(values);
		element.shapes[0].beginSetter = {
			x: element.shapes[0].begin.x,
			y: Utils.round(element.shapes[0].begin.y + yDelta),
			z: Utils.round(element.depend.getBoundingZ().max + values.hangBarOffset)
		};
		ElementGenerator.updateExtensions(element, values);

		equipment.allElements
			.filter((el: Element) => el.depend == element)
			.filter((el: Element) => el.parent.is(EquipmentElements.Hook))
			.forEach((el: Element) => {
				el.shapes[0].beginSetter = {
					x: el.shapes[0].begin.x,
					y: Utils.round(element.shapes[0].begin.y - el.shapes[0].height / 2),
					z: element.getBoundingZ().max
				};
				ElementGenerator.updateExtensions(el, { availability: values.availability });
			});

		return equipment;
	}

	protected getShapes(): Array<Cylinder> {
		const x: number = this.point.x,
			y: number = Utils.round(this.equipment.Height - this.point.y),
			base: Element = this.equipment.bases.find((i: Element) => i.shapes[0].isPointOn(this.point.x));

		let elements: Array<Element> = this.equipment.allElements
				.filter((el: Element) => el != this.dependElement && el.parent.type != EquipmentElements.SideWalls),
			shapes: Array<Bounding> = [].concat(...elements.map((element: Element) => element.shapes))
				.filter((shape: Figure) => shape.isPointOn(x, y, null))
				.map((shape: Figure) => shape.getBoundingX());

		const baseBoundingX: Bounding = base.getBoundingX();

		if (shapes.length) {
			let distances: Array<Array<number>> = [];

			shapes = shapes.sort((a: Bounding, b: Bounding) => a.min - b.min);
			shapes.forEach((shape: Bounding, idx: number) => {
				if (idx == 0) distances.push([baseBoundingX.min, shape.min]);
				if (idx == shapes.length - 1) {
					distances.push([shape.max, baseBoundingX.max]);
					return;
				}
				distances.push([shape.max, shapes[idx + 1].min]);
			});

			const distance: Array<number> = distances.find((d: Array<number>) => x >= d[0] && x <= d[1]);

			if (distance) {
				baseBoundingX.min = distance[0];
				baseBoundingX.max = distance[1];
			}
		}

		let zCoordinate: number = UnitConverter.convertAndRoundToSaveValue(base.layoutBoundingZ.max - 0.01);

		if (zCoordinate <= this.dependElement.getBoundingZ().max) {
			zCoordinate = UnitConverter.convertAndRoundToSaveValue(this.dependElement.getBoundingZ().max + 0.39);
		}

		const begin: Point = {
				x: this.dependElement.getBoundingX().min,
				y: y,
				z: zCoordinate
			},
			figure: Cylinder = new Cylinder({
				begin,
				width: this.dependElement.Width,
				diameter: UnitConverter.convertAndRoundToSaveValue(0.02)
			});

		return [figure];
	}

	protected getExtensions(): Array<Extension<any>> {
		return HangBarGenerator.getExtensions().map((extension: any) => {
			const { type, active, ...params } = extension;
			return new Extension<any>(type, active, params);
		});
	}
}

class BoxGenerator extends ElementGenerator {

	static getOptions(box: Element, equipment: EquipmentEditable): { [key: string]: Options } {
		let maxWallWidth: number = Utils.round(Math.min(box.depend.shapes[0].depth, box.depend.shapes[0].width) / 2);
		let minDimensions: number = Utils.round(box.depend.shapes[0].wallWidth * 2);

		return {
			length: {
				...DEFAULT_OPTION,
				value: box.shapes[0].width,
				valueChange() {
					if (this.value < MIN_DIMENSION_VALUE) {
						this.valid = false;
						this.error = 'UNABLE_VALUE';
					} else if (this.value > box.depend.shapes[0].width) {
						this.valid = false;
						this.error = 'BOX_WIDTH_WARNING';
					/*} else if (this.value < minDimensions) {
						this.valid = false;
						this.error = 'UNABLE_VALUE';*/
					} else {
						this.valid = true;
						this.error = '';
					}
					this.dirty = this.value != box.shapes[0].width;
					maxWallWidth = Utils.round(Math.min(box.depend.shapes[0].depth, this.value) / 2);
				}
			},
			height: {
				...DEFAULT_OPTION,
				value: box.shapes[0].height,
				valueChange() {
					if (this.value < MIN_DIMENSION_VALUE) {
						this.valid = false;
						this.error = 'UNABLE_VALUE';
					} else {
						this.valid = true;
						this.error = '';
					}
					this.dirty = this.value != box.shapes[0].height;
				}
			},
			depth: {
				...DEFAULT_OPTION,
				value: box.shapes[0].depth,
				valueChange() {
					if (this.value < MIN_DIMENSION_VALUE) {
						this.valid = false;
						this.error = 'UNABLE_VALUE';
					} else if (this.value > box.depend.shapes[0].depth) {
						this.valid = false;
						this.error = 'SHELF_DEPTH_WARNING';
					/*} else if (this.value < minDimensions) {
						this.valid = false;
						this.error = 'UNABLE_VALUE';*/
					} else {
						this.valid = true;
						this.error = '';
					}
					this.dirty = this.value != box.shapes[0].depth;
					maxWallWidth = Utils.round(Math.min(this.value, box.depend.shapes[0].width) / 2);
				}
			},
			wallWidth: {
				...DEFAULT_OPTION,
				value: box.shapes[0].wallWidth,
				valueChange() {
					if (this.value < MIN_DIMENSION_VALUE) {
						this.valid = false;
						this.error = 'UNABLE_VALUE';
					/*} else if (this.value > maxWallWidth) {
						this.valid = false;
						this.error = 'UNABLE_VALUE';*/
					} else {
						this.valid = true;
						this.error = '';
					}
					this.dirty = this.value != box.shapes[0].wallWidth;
					minDimensions = Utils.round(this.value * 2);
				}
			}
		}
	}

	static getExtensions(): Array<any> {
		return [
			{
				type: ExtensionType.Availability,
				active: true
			}
		];
	};

	static updateElement(element: Element, equipment: EquipmentEditable, values: {
		height: number,
		depth: number,
		width: number,
		wallWidth: number,
	}) {
		const isBox = ['Box', 'SlicedBox'].includes(element.parent.type);
		values.wallWidth = Math.min(
			values.wallWidth,
			isBox
				? values.wallWidth
				: Math.min(values.height, values.depth, values.width) / 2
		);
		element.shapes[0].update(values);

		return equipment;
	};

	protected getShapes() {
		const rect: Rectangle = new Rectangle({
			begin: { x: this.point.x, y: this.dependElement.getBoundingY().max, z: this.point.y },
			width: UnitConverter.convertAndRoundToSaveValue(0.2),
			depth: UnitConverter.convertAndRoundToSaveValue(0.2),
			height: UnitConverter.convertAndRoundToSaveValue(0.2),
			empty: {
				front: true,
				top: true
			}
		});

		return [rect];
	}

	protected getExtensions(): Array<Extension<any>> {
		return BoxGenerator.getExtensions().map((extension: any) => {
			const { type, active, ...params } = extension;
			return new Extension<any>(type, active, params);
		});
	}
}

class SlicedBoxGenerator extends ElementGenerator {

	static getOptions(box: Element, equipment: EquipmentEditable): { [key: string]: Options } {
		let maxWallWidth: number = Utils.round(Math.min(box.depend.shapes[0].depth, box.depend.shapes[0].width) / 2);
		let minDimensions: number = Utils.round(box.depend.shapes[0].wallWidth * 2);

		return {
			length: {
				...DEFAULT_OPTION,
				value: box.shapes[0].width,
				valueChange() {
					if (this.value < MIN_DIMENSION_VALUE) {
						this.valid = false;
						this.error = 'UNABLE_VALUE';
					} else if (this.value > box.depend.shapes[0].width) {
						this.valid = false;
						this.error = 'BOX_WIDTH_WARNING';
					/*} else if (this.value < minDimensions) {
						this.valid = false;
						this.error = 'UNABLE_VALUE';*/
					} else {
						this.valid = true;
						this.error = '';
					}
					this.dirty = this.value != box.shapes[0].width;
					maxWallWidth = Utils.round(Math.min(box.depend.shapes[0].depth, this.value) / 2);
				}
			},
			frontHeight: {
				...DEFAULT_OPTION,
				value: box.shapes[0].frontHeight,
				valueChange() {
					if (this.value < MIN_DIMENSION_VALUE) {
						this.valid = false;
						this.error = 'UNABLE_VALUE';
					} else {
						this.valid = true;
						this.error = '';
					}
					this.dirty = this.value != box.shapes[0].frontHeight;
				}
			},
			backHeight: {
				...DEFAULT_OPTION,
				value: box.shapes[0].backHeight,
				valueChange() {
					if (this.value < MIN_DIMENSION_VALUE) {
						this.valid = false;
						this.error = 'UNABLE_VALUE';
					} else {
						this.valid = true;
						this.error = '';
					}
					this.dirty = this.value != box.shapes[0].backHeight;
				}
			},
			depth: {
				...DEFAULT_OPTION,
				value: box.shapes[0].depth,
				valueChange() {
					if (this.value < MIN_DIMENSION_VALUE) {
						this.valid = false;
						this.error = 'UNABLE_VALUE';
					} else if (this.value > box.depend.shapes[0].depth) {
						this.valid = false;
						this.error = 'SHELF_DEPTH_WARNING';
					/*} else if (this.value < minDimensions) {
						this.valid = false;
						this.error = 'UNABLE_VALUE';*/
					} else {
						this.valid = true;
						this.error = '';
					}
					this.dirty = this.value != box.shapes[0].depth;
					maxWallWidth = Utils.round(Math.min(this.value, box.depend.shapes[0].width) / 2);
				}
			},
			wallWidth: {
				...DEFAULT_OPTION,
				value: box.shapes[0].wallWidth,
				valueChange() {
					if (this.value < MIN_DIMENSION_VALUE) {
						this.valid = false;
						this.error = 'UNABLE_VALUE';
					/*} else if (this.value > maxWallWidth) {
						this.valid = false;
						this.error = 'UNABLE_VALUE';*/
					} else {
						this.valid = true;
						this.error = '';
					}
					this.dirty = this.value != box.shapes[0].wallWidth;
					minDimensions = Utils.round(this.value * 2);
				}
			}
		}
	}

	static getExtensions(): Array<any> {
		return [
			{
				type: ExtensionType.Availability,
				active: true
			}
		];
	};

	static updateElement(element: Element, equipment: EquipmentEditable, values: {
		frontHeight: number,
		backHeight: number,
		depth: number,
		width: number,
		wallWidth: number,
	}) {
		const isBox = ['Box', 'SlicedBox'].includes(element.parent.type);
		values.wallWidth = Math.min(
			values.wallWidth,
			isBox
				? values.wallWidth
				: Math.min(
				values.frontHeight,
				values.backHeight,
				values.depth,
				values.width
			) / 2
		);
		element.shapes[0].update(values);

		return equipment;
	};

	protected getShapes() {
		const rect: RectangleSliced = new RectangleSliced({
			begin: { x: this.point.x, y: this.dependElement.getBoundingY().max, z: this.point.y },
			width: UnitConverter.convertAndRoundToSaveValue(0.2),
			depth: UnitConverter.convertAndRoundToSaveValue(0.2),
			frontHeight: UnitConverter.convertAndRoundToSaveValue(0.1),
			backHeight: UnitConverter.convertAndRoundToSaveValue(0.2),
			empty: {
				front: true,
				top: true
			}
		});

		return [rect];
	}

	protected getExtensions(): Array<Extension<any>> {
		return SlicedBoxGenerator.getExtensions().map((extension: any) => {
			const { type, active, ...params } = extension;
			return new Extension<any>(type, active, params);
		});
	}
}

class HookPanelGenerator extends ElementGenerator {

	static getOptions(panel: Element, equipment: EquipmentEditable): { [key: string]: Options } {
		return {
			length: {
				...DEFAULT_OPTION,
				value: panel.shapes[0].width,
				valueChange() {
					if (this.value < MIN_DIMENSION_VALUE) {
						this.valid = false;
						this.error = 'UNABLE_VALUE';
					} else if (this.value > panel.depend.shapes[0].width) {
						this.valid = false;
						this.error = 'HOOKPANEL_WIDTH_WARNING';
					} else {
						this.valid = true;
						this.error = '';
					}
					this.dirty = this.value != panel.shapes[0].width;
				}
			},
			height: {
				...DEFAULT_OPTION,
				value: panel.shapes[0].height,
				valueChange() {
					if (this.value < MIN_DIMENSION_VALUE) {
						this.valid = false;
						this.error = 'UNABLE_VALUE';
					} else if (this.value > panel.depend.shapes[0].height) {
						this.valid = false;
						this.error = 'SHELF_HEIGHT_WARNING';
					} else {
						this.valid = true;
						this.error = '';
					}
					this.dirty = this.value != panel.shapes[0].height;
				}
			},
			depth: {
				...DEFAULT_OPTION,
				value: panel.shapes[0].depth,
				valueChange() {
					if (this.value < MIN_DIMENSION_VALUE) {
						this.valid = false;
						this.error = 'UNABLE_VALUE';
					} else if (this.value > panel.depend.depend.shapes[0].depth) {
						this.valid = false;
						this.error = 'HOOKPANEL_DEPTH_WARNING';
					} else {
						this.valid = true;
						this.error = '';
					}
					this.dirty = this.value != panel.shapes[0].depth;
				}
			},
			hooksAutolayout: {
				...DEFAULT_OPTION,
				value: false,
				valueChange() {
					this.dirty = this.value != (panel.shapes[0] as Rectangle).hooksAutolayout;
				}
			},
			distanceBetweenHooks: {
				...DEFAULT_OPTION,
				value: (panel.shapes[0] as Rectangle).distanceBetweenHooks,
				valueChange() {
					if (this.value < MIN_DIMENSION_VALUE || this.value > panel.shapes[0].width) {
						this.valid = false;
						this.error = 'UNABLE_VALUE';
					} else {
						this.valid = true;
						this.error = '';
					}
					this.dirty = this.value != (panel.shapes[0] as Rectangle).distanceBetweenHooks;
				}
			},
			distanceBetweenRowsHooks: {
				...DEFAULT_OPTION,
				value: (panel.shapes[0] as Rectangle).distanceBetweenRowsHooks,
				valueChange() {
					if (this.value < MIN_DIMENSION_VALUE || this.value > panel.shapes[0].height) {
						this.valid = false;
						this.error = 'UNABLE_VALUE';
					} else {
						this.valid = true;
						this.error = '';
					}
					this.dirty = this.value != (panel.shapes[0] as Rectangle).distanceBetweenRowsHooks;
				}
			},
			firstHookTopDistance: {
				...DEFAULT_OPTION,
				value: (panel.shapes[0] as Rectangle).firstHookTopDistance,
				valueChange() {
					if (this.value < MIN_DIMENSION_VALUE || this.value > panel.shapes[0].height) {
						this.valid = false;
						this.error = 'UNABLE_VALUE';
					} else {
						this.valid = true;
						this.error = '';
					}
					this.dirty = this.value != (panel.shapes[0] as Rectangle).firstHookTopDistance;
				}
			},
			firstHookLeftDistance: {
				...DEFAULT_OPTION,
				value: (panel.shapes[0] as Rectangle).firstHookLeftDistance,
				valueChange() {
					if (this.value < MIN_DIMENSION_VALUE || this.value > panel.shapes[0].width) {
						this.valid = false;
						this.error = 'UNABLE_VALUE';
					} else {
						this.valid = true;
						this.error = '';
					}
					this.dirty = this.value != (panel.shapes[0] as Rectangle).firstHookLeftDistance;
				}
			}
		}
	}

	static updateElement(element: Element, equipment: EquipmentEditable, values: {
		height: number,
		width: number,
		depth: number,
		distanceBetweenHooks?: number,
		distanceBetweenRowsHooks?: number,
		firstHookTopDistance?: number,
		firstHookLeftDistance?: number,
		hooksAutolayout?: boolean
	}) {
		const zDelta: number = values.depth - element.shapes[0].depth;
		element.shapes[0].update(values);

		equipment.allElements
			.filter(el => el.getDepends().includes(element))
			.forEach(el => {
				el.shapes.forEach(shape => {
					shape.begin.z += zDelta;
				});
			});

		return equipment;
	};

	protected getShapes() {
		const base = this.equipment.bases.find(i => i.shapes[0].isPointOn(this.point.x)),
			x = this.point.x,
			y = this.equipment.Height - this.point.y,
			baseBoundingZ = base.getBoundingZ();

		if (['BackWall'].includes(this.dependElement.parent.type)) {
			baseBoundingZ.min = this.dependElement.getBoundingZ().max;
		}

		const begin = {
				x: Utils.round(x - 0.1),
				y: Utils.round(y - 0.1),
				z: baseBoundingZ.min
			},
			rect = new Rectangle({
				begin,
				width: UnitConverter.convertAndRoundToSaveValue(0.2),
				depth: UnitConverter.convertAndRoundToSaveValue(0.01),
				height: UnitConverter.convertAndRoundToSaveValue(0.2),
				empty: {
					front: true,
					top: false
				}
			});

		return [rect];
	}

	protected getExtensions(): Array<Extension<any>> {
		return [];
	}
}

class HookGenerator extends ElementGenerator {

	static getOptions(hook: Element, equipment: EquipmentEditable): { [key: string]: Options } {
		let dependHeight: number = hook.depend.shapes[0].height;

		if (hook.depend.parent.is(EquipmentElements.HangBar)) {
			dependHeight = (hook.depend.shapes[0] as Cylinder).diameter;
		}

		return {
			width: {
				...DEFAULT_OPTION,
				value: hook.shapes[0].width,
				valueChange() {
					if (this.value < MIN_DIMENSION_VALUE) {
						this.valid = false;
						this.error = 'UNABLE_VALUE';
					} else if (this.value > hook.depend.shapes[0].width) {
						this.valid = false;
						this.error = 'HOOK_WIDTH_WARNING';
					} else {
						this.valid = true;
						this.error = '';
					}
					this.dirty = this.value != hook.shapes[0].width;
				}
			},
			height: {
				...DEFAULT_OPTION,
				value: hook.shapes[0].height,
				valueChange() {
					if (this.value < MIN_DIMENSION_VALUE) {
						this.valid = false;
						this.error = 'UNABLE_VALUE';
					} else if (this.value > dependHeight) {
						this.valid = false;
						this.error = 'HOOK_HEIGHT_WARNING';
					} else {
						this.valid = true;
						this.error = '';
					}
					this.dirty = this.value != hook.shapes[0].height;
				}
			},
			depth: {
				...DEFAULT_OPTION,
				value: hook.shapes[0].depth,
				valueChange() {
					if (this.value < MIN_DIMENSION_VALUE) {
						this.valid = false;
						this.error = 'UNABLE_VALUE';
					} else {
						this.valid = true;
						this.error = '';
					}
					this.dirty = this.value != hook.shapes[0].depth;
				}
			}
		}
	}

	static getExtensions(): Array<any> {
		return [
			{
				type: ExtensionType.Availability,
				active: true
			}
		];
	};

	static updateElement(element: Element, equipment: EquipmentEditable, values: {
		height: number,
		width: number,
		depth: number,
	}) {
		element.shapes[0].update(values);

		return equipment;
	};

	protected getShapes() {
		const base: Element = this.equipment.bases.find((base: Element) => base.shapes[0].isPointOn(this.point.x));
		const baseBoundingZ: Bounding = base.getBoundingZ();

		baseBoundingZ.min = this.dependElement.getBoundingZ().max;

		let hookDepth: number = Utils.round(baseBoundingZ.max - baseBoundingZ.min);
		let beginY: number = Utils.round(this.equipment.Height - this.point.y - 0.005);

		if (hookDepth <= 0) hookDepth = UnitConverter.convertAndRoundToSaveValue(0.4);

		if (this.dependElement.parent.is(EquipmentElements.HangBar)) {
			beginY = Utils.round(this.dependElement.shapes[0].begin.y - 0.005);
		}

		const rect: Rectangle = new Rectangle({
			begin: {
				x: Utils.round(this.point.x - 0.005),
				y: beginY,
				z: baseBoundingZ.min
			},
			width: UnitConverter.convertAndRoundToSaveValue(0.01),
			depth: hookDepth,
			height: UnitConverter.convertAndRoundToSaveValue(0.01),
			empty: {
				front: false,
				top: false
			}
		});

		return [rect];
	}

	protected getExtensions(): Array<Extension<any>> {
		return HookGenerator.getExtensions().map((extension: any) => {
			const { type, active, ...params } = extension;
			return new Extension<any>(type, active, params);
		});
	}
}

class PerforationGenerator extends ElementGenerator {

	static getOptions(panel: Element, equipment: EquipmentEditable): { [key: string]: Options } {
		return {
			length: {
				...DEFAULT_OPTION,
				value: panel.shapes[0].width,
				valueChange() {
					if (this.value < MIN_DIMENSION_VALUE) {
						this.valid = false;
						this.error = 'UNABLE_VALUE';
					} else if (this.value > panel.depend.shapes[0].width) {
						this.valid = false;
						this.error = 'PERFORATION_WIDTH_WARNING';
					} else {
						this.valid = true;
						this.error = '';
					}
					this.dirty = this.value != panel.shapes[0].width;
				}
			},
			height: {
				...DEFAULT_OPTION,
				value: panel.shapes[0].height,
				valueChange() {
					if (this.value < MIN_DIMENSION_VALUE) {
						this.valid = false;
						this.error = 'UNABLE_VALUE';
					} else if (this.value > panel.depend.shapes[0].height) {
						this.valid = false;
						this.error = 'SHELF_HEIGHT_WARNING_PERFORATION';
					} else {
						this.valid = true;
						this.error = '';
					}
					this.dirty = this.value != panel.shapes[0].height;
				}
			},
			depth: {
				...DEFAULT_OPTION,
				value: panel.shapes[0].depth,
				valueChange() {
					if (this.value < MIN_DIMENSION_VALUE || this.value > panel.depend.shapes[0].depth) {
						this.valid = false;
						this.error = 'UNABLE_VALUE';
					} else if (this.value > panel.depend.depend.shapes[0].depth) {
						this.valid = false;
						this.error = 'PERFORATION_DEPTH_WARNING';
					} else {
						this.valid = true;
						this.error = '';
					}
					this.dirty = this.value != panel.shapes[0].depth;
				}
			},
			distanceBetweenHoles: {
				...DEFAULT_OPTION,
				value: (panel.shapes[0] as Rectangle).distanceBetweenHoles,
				valueChange() {
					if (this.value < MIN_DIMENSION_VALUE || this.value > panel.shapes[0].width) {
						this.valid = false;
						this.error = 'UNABLE_VALUE';
					} else {
						this.valid = true;
						this.error = '';
					}
					this.dirty = this.value != (panel.shapes[0] as Rectangle).distanceBetweenHoles;
				}
			},
			distanceBetweenRowsHoles: {
				...DEFAULT_OPTION,
				value: (panel.shapes[0] as Rectangle).distanceBetweenRowsHoles,
				valueChange() {
					if (this.value < MIN_DIMENSION_VALUE || this.value > panel.shapes[0].height) {
						this.valid = false;
						this.error = 'UNABLE_VALUE';
					} else {
						this.valid = true;
						this.error = '';
					}
					this.dirty = this.value != (panel.shapes[0] as Rectangle).distanceBetweenRowsHoles;
				}
			},
			firstHoleTopDistance: {
				...DEFAULT_OPTION,
				value: (panel.shapes[0] as Rectangle).firstHoleTopDistance,
				valueChange() {
					if (this.value < MIN_DIMENSION_VALUE || this.value > panel.shapes[0].height) {
						this.valid = false;
						this.error = 'UNABLE_VALUE';
					} else {
						this.valid = true;
						this.error = '';
					}
					this.dirty = this.value != (panel.shapes[0] as Rectangle).firstHoleTopDistance;
				}
			},
			firstHoleLeftDistance: {
				...DEFAULT_OPTION,
				value: (panel.shapes[0] as Rectangle).firstHoleLeftDistance,
				valueChange() {
					if (this.value < MIN_DIMENSION_VALUE || this.value > panel.shapes[0].width) {
						this.valid = false;
						this.error = 'UNABLE_VALUE';
					} else {
						this.valid = true;
						this.error = '';
					}
					this.dirty = this.value != (panel.shapes[0] as Rectangle).firstHoleLeftDistance;
				}
			}
		}
	}

	static updateElement(element: Element, equipment: EquipmentEditable, values: {
		height: number,
		length: number,
		depth: number,
		distanceBetweenHoles: number,
		distanceBetweenRowsHoles: number,
		firstHoleTopDistance: number,
		firstHoleLeftDistance: number,
	}) {
		const zDelta: number = values.depth - element.shapes[0].depth;
		element.shapes[0].update(values);

		equipment.allElements
			.filter(el => el.getDepends().includes(element))
			.forEach(el => {
				el.shapes.forEach(shape => {
					shape.begin.z += zDelta;
				});
			});

		return equipment;
	};

	protected getShapes() {
		const base = this.equipment.bases.find(i => i.shapes[0].isPointOn(this.point.x)),
			x = this.point.x,
			y = this.equipment.Height - this.point.y,
			baseBoundingZ = base.getBoundingZ();

		if (['BackWall'].includes(this.dependElement.parent.type)) {
			baseBoundingZ.min = this.dependElement.getBoundingZ().max;
		}

		const begin = {
				x: Utils.round(x - 0.1),
				y: Utils.round(y - 0.1),
				z: baseBoundingZ.min
			},
			rect = new Rectangle({
				begin,
				width: UnitConverter.convertAndRoundToSaveValue(0.2),
				depth: UnitConverter.convertAndRoundToSaveValue(0.01),
				height: UnitConverter.convertAndRoundToSaveValue(0.2),
				empty: {
					front: true,
					top: false
				}
			});

		return [rect];
	}

	protected getExtensions(): Array<Extension<any>> {
		return [];
	}
}

class HoleGenerator extends ElementGenerator {

	static getOptions(hole: Element, equipment: EquipmentEditable): { [key: string]: Options } {
		return {};
	};

	static updateElement(element: Element, equipment: EquipmentEditable, values: {
		height: number,
		width: number,
		depth: number,
	}) {
		element.shapes[0].update(values);

		return equipment;
	};

	protected getShapes() {
		const base = this.equipment.bases.find(i => i.shapes[0].isPointOn(this.point.x)),
			x = this.point.x,
			y = this.equipment.Height - this.point.y,

			baseBoundingZ = base.getBoundingZ();

		baseBoundingZ.min = this.dependElement.getBoundingZ().max;

		const begin = {
				x: Utils.round(x - 0.005),
				y: Utils.round(y - 0.005),
				z: this.dependElement.getBoundingZ().max
			},
			rect = new Rectangle({
				begin,
				width: UnitConverter.convertAndRoundToSaveValue(0.01),
				depth: this.dependElement.Depth,
				height: UnitConverter.convertAndRoundToSaveValue(0.01),
				empty: {
					front: false,
					top: false
				}
			});

		return [rect];
	}

	protected getExtensions(): Array<Extension<any>> {
		return [];
	}
}

class PartitionGenerator extends ElementGenerator {

	static getOptions(partition: Element, equipment: EquipmentEditable): { [key: string]: Options } {
		return {
			width: {
				...DEFAULT_OPTION,
				value: partition.shapes[0].width,
				valueChange() {
					if (this.value < MIN_DIMENSION_VALUE || this.value > partition.depend.shapes[0].width) {
						this.valid = false;
						this.error = 'UNABLE_VALUE';
					} else {
						this.valid = true;
						this.error = '';
					}
					this.dirty = this.value != partition.shapes[0].width;
				}
			},
			height: {
				...DEFAULT_OPTION,
				value: partition.shapes[0].height,
				valueChange() {
					if (this.value < MIN_DIMENSION_VALUE) {
						this.valid = false;
						this.error = 'UNABLE_VALUE';
					} else {
						this.valid = true;
						this.error = '';
					}
					this.dirty = this.value != partition.shapes[0].height;
				}
			},
			depth: {
				...DEFAULT_OPTION,
				value: partition.shapes[0].depth,
				valueChange() {
					if (this.value < MIN_DIMENSION_VALUE || this.value > partition.depend.shapes[0].depth) {
						this.valid = false;
						this.error = 'UNABLE_VALUE';
					} else {
						this.valid = true;
						this.error = '';
					}
					this.dirty = this.value != partition.shapes[0].depth;
				}
			}
		}
	}

	static getExtensions(): Array<any> {
		return [
			{
				type: ExtensionType.Availability,
				active: true
			}
		];
	};

	static updateElement(element: Element, equipment: EquipmentEditable, values: {
		height: number,
		width: number,
		depth: number,
	}) {
		element.shapes[0].update(values);

		return equipment;
	};

	protected getShapes() {
		const rect: Rectangle = new Rectangle({
			begin: {
				x: this.point.x,
				y: this.dependElement.getBoundingY().max,
				z: this.dependElement.shapes[0].begin.z
			},
			width: UnitConverter.convertAndRoundToSaveValue(0.01),
			depth: this.dependElement.shapes[0].depth,
			height: UnitConverter.convertAndRoundToSaveValue(0.2),
			empty: {
				front: false,
				top: false
			},
			fillColor: 'grey'
		});

		return [rect];
	}

	protected getExtensions(): Array<Extension<any>> {
		return PartitionGenerator.getExtensions().map((extension: any) => {
			const { type, active, ...params } = extension;
			return new Extension<any>(type, active, params);
		});
	}
}

function createElement(parentType: EquipmentElements, shapes: Array<any>, extensions: Array<any> = [], id?: string): Element {
	const defaultExtensions: Array<any> = elementsGenerator[parentType].getExtensions();

	const elementShapes: Array<Figure> = shapes.map((shape: any) => {
			switch (shape.type) {
				case 'rectangle': return new Rectangle(shape);
				case 'trapeze': return new Trapeze(shape);
				case 'rectangle-sliced': return new RectangleSliced(shape);
				case 'cylinder': return new Cylinder(shape);
			}
		}),
		elementExtensions: Array<Extension<any>> = defaultExtensions.map((extension: any) => {
			const { type: defaultType, active: defaultActive, ...defaultParams } = extension;
			const elementExtension: any = extensions.find(((ex: any) => ex.type == defaultType));

			if (elementExtension) {
				const { type, active, ...params } = elementExtension;
				return new Extension<any>(type, active, Object.assign(defaultParams, params));
			}

			return new Extension<any>(defaultType, defaultActive, defaultParams);
		}),
		elementList: ElementsList = new ElementsList(),
		parent: EquipmentElement = elementList.getParent(parentType);

	return new Element(parent, elementShapes, elementExtensions, null, id);
}

export const elementsGenerator = {
	[EquipmentElements.BaseRectangle]: RectangleBaseGenerator,
	[EquipmentElements.BaseTrapeze]: TrapezeBaseGenerator,
	[EquipmentElements.BackWall]: BackWallGenerator,
	[EquipmentElements.CentralPillar]: CentralPillarGenerator,
	[EquipmentElements.HookWall]: HookWallGenerator,
	[EquipmentElements.PerforatedWall]: PerforatedWallGenerator,
	[EquipmentElements.SideWalls]: SidesWallGenerator,
	[EquipmentElements.Showcase]: ShowcaseGenerator,
	[EquipmentElements.Shelf]: ShelfGenerator,
	[EquipmentElements.AngleShelf]: AngleShelfGenerator,
	[EquipmentElements.HangBar]: HangBarGenerator,
	[EquipmentElements.Box]: BoxGenerator,
	[EquipmentElements.SlicedBox]: SlicedBoxGenerator,
	[EquipmentElements.HookPanel]: HookPanelGenerator,
	[EquipmentElements.Hook]: HookGenerator,
	[EquipmentElements.Perforation]: PerforationGenerator,
	[EquipmentElements.Hole]: HoleGenerator,
	[EquipmentElements.Partition]: PartitionGenerator,
	createElement
};
